<template>
    <AppLayout viewkey="packages_builder">
        <div class="row justify-center">
            <div :class="['col-12', step === 4 ? 'col-md-12 col-lg-11' : 'col-md-10 col-lg-8']" style="transition: max-width 0.3s ease;">
                <!-- Header -->
                <div class="row items-center q-mb-sm">
                    <q-btn flat round icon="arrow_back" href="/Packages" />
                    <div class="text-h5 q-ml-md">{{ pageTitle }}</div>
                </div>

                <!-- Error Banner -->
                <q-banner v-if="error" class="bg-negative text-white q-mb-md" rounded>
                    <template v-slot:avatar>
                        <q-icon name="warning" />
                    </template>
                    {{ error }}
                    <template v-slot:action>
                        <q-btn flat label="Dismiss" @click="clearError" />
                    </template>
                </q-banner>

                <!-- Loading overlay for initial load -->
                <div v-if="initialLoading" class="text-center q-pa-xl">
                    <q-spinner size="48px" color="primary" />
                    <div class="text-body1 text-grey-7 q-mt-md">{{ mode === 'template' ? 'Loading template...' : 'Loading package...' }}</div>
                </div>

                <q-stepper v-else v-model="step" ref="stepper" animated color="primary" header-nav
                    alternative-labels flat bordered
                    transition-prev="slide-right" transition-next="slide-left"
                    class="packages-stepper"
                >
                    <!-- Step 1: Package Details -->
                    <q-step :name="1" title="Package Details" icon="inventory_2" :done="step > 1 || isEditing" :header-nav="step > 1">
                        <div class="text-h6 q-mb-xs">Package Details</div>
                        <div class="text-body2 text-grey-7 q-mb-lg">Name your package and configure notification settings for recipients.</div>
                        <q-card bordered>
                            <q-card-section class="bg-grey-1">
                                <div class="row items-center no-wrap">
                                    <q-icon name="inventory_2" size="sm" color="primary" class="q-mr-sm" />
                                    <div>
                                        <div class="text-subtitle1 text-weight-bold">General Information</div>
                                        <div class="text-caption text-grey-7">Configure the package name, notification settings, and options.</div>
                                    </div>
                                </div>
                            </q-card-section>
                            <q-separator />
                            <q-card-section>
                                <div class="row q-col-gutter-md">
                                    <div class="col-12">
                                        <q-input
                                            v-model="basicInfo.name"
                                            outlined
                                            label="Package Name *"
                                            hint="A name to identify this package"
                                            :rules="[val => !!val || 'Package name is required']"
                                        />
                                    </div>
                                    <div class="col-12">
                                        <q-input
                                            v-model="basicInfo.emailSubject"
                                            outlined
                                            label="Email Subject"
                                            hint="Subject line for notification emails"
                                        />
                                    </div>
                                    <div class="col-12">
                                        <q-input
                                            v-model="basicInfo.emailMessage"
                                            outlined
                                            type="textarea"
                                            label="Message to Signers"
                                            hint="Optional message included in signing invitation"
                                            rows="3"
                                        />
                                    </div>
                                    <div class="col-12 col-md-6">
                                        <q-input
                                            v-model="basicInfo.expirationDate"
                                            outlined
                                            label="Expiration Date"
                                            hint="Leave empty for no expiration"
                                            :error="!!expirationDateError"
                                            :error-message="expirationDateError"
                                        >
                                            <template v-slot:append>
                                                <q-icon name="calendar_today" class="cursor-pointer">
                                                    <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                                                        <q-date
                                                            v-model="basicInfo.expirationDate"
                                                            mask="YYYY-MM-DD"
                                                            :options="expirationDatePickerOptions"
                                                        />
                                                    </q-popup-proxy>
                                                </q-icon>
                                            </template>
                                        </q-input>
                                    </div>
                                    <div class="col-12 col-md-6">
                                        <q-toggle
                                            v-model="basicInfo.enforceSigningOrder"
                                            label="Enforce signing order"
                                            :disable="basicInfo.enableInstantBankTransfer"
                                        >
                                            <q-tooltip v-if="basicInfo.enableInstantBankTransfer">
                                                Sequential signing is required for Instant Bank Payments.
                                            </q-tooltip>
                                        </q-toggle>
                                        <div class="text-caption text-grey-7">
                                            Signers must sign in the specified order
                                        </div>
                                    </div>
                                    <!-- Instant Bank Payments toggle.
                                         Controlled binding (:model-value + @update:model-value) so the
                                         visual state is strictly driven by the store. OFF → ON routes
                                         through the same IBP classifier the send-time gauntlet uses
                                         (offer / fixup / conflict / etc.) and persists on accept; on
                                         decline or edit the store stays at false and the toggle reverts
                                         visually. ON → OFF runs the existing disable-confirm flow,
                                         which preserves prior signer/order adjustments per invariant 19. -->
                                    <div class="col-12 col-md-6">
                                        <q-toggle
                                            :model-value="basicInfo.enableInstantBankTransfer"
                                            label="Instant Bank Payments"
                                            color="indigo-8"
                                            icon="account_balance"
                                            :disable="!paymentCollectionEnabled"
                                            @update:model-value="onIbpToggleChange"
                                        />
                                        <div class="text-caption text-grey-7">
                                            Instant Bank Transfer payments are available for packages with a single paying signer (who always signs last).
                                        </div>
                                        <div v-if="!paymentCollectionEnabled" class="text-caption text-orange-9">
                                            Set up Stripe in Settings to enable payment collection first.
                                        </div>
                                    </div>
                                    <div class="col-12">
                                        <q-toggle
                                            v-model="basicInfo.reminderEnabled"
                                            label="Send automatic reminders"
                                        />
                                        <div class="text-caption text-grey-7">
                                            Automatically remind signers who haven't completed signing
                                        </div>
                                    </div>
                                    <div v-if="basicInfo.reminderEnabled" class="col-12 col-md-6">
                                        <q-slider
                                            v-model="basicInfo.reminderIntervalDays"
                                            :min="1" :max="14" :step="1"
                                            label label-always markers snap
                                            color="primary"
                                            class="q-mt-lg q-mx-md"
                                        />
                                        <div class="text-caption text-center text-grey-7">
                                            Send reminder every {{ basicInfo.reminderIntervalDays }} day(s)
                                        </div>
                                    </div>
                                </div>
                            </q-card-section>
                        </q-card>
                        <q-stepper-navigation>
                            <q-btn @click="onNextStep" color="primary" label="Continue" push :disable="!canProceedToStep2 || loading" :loading="loading">
                                <q-tooltip v-if="stepBlockReason(1)">{{ stepBlockReason(1) }}</q-tooltip>
                            </q-btn>
                        </q-stepper-navigation>
                    </q-step>

                    <!-- Step 2: Documents -->
                    <q-step :name="2" title="Documents" icon="article" :done="step > 2 || isEditing" :header-nav="step > 2">
                        <div class="text-h6 q-mb-xs">Upload Documents</div>
                        <div class="text-body2 text-grey-7 q-mb-lg">Add the documents that need to be signed. Supported formats: PDF, DOC, DOCX.</div>
                        <q-card bordered class="q-mb-md">
                            <q-card-section class="bg-grey-1">
                                <div class="row items-center no-wrap">
                                    <q-icon name="cloud_upload" size="sm" color="primary" class="q-mr-sm" />
                                    <div>
                                        <div class="text-subtitle1 text-weight-bold">Upload Files</div>
                                        <div class="text-caption text-grey-7">PDF or Word documents. Text tags are auto-detected after upload.</div>
                                    </div>
                                </div>
                            </q-card-section>
                            <q-separator />
                            <q-card-section>
                                <q-banner class="bg-blue-1 q-mb-md" rounded>
                                    <template v-slot:avatar>
                                        <q-icon name="info" color="primary" />
                                    </template>
                                    Upload PDF documents containing text tags like
                                    <code v-text="tagExamples.sig"></code>,
                                    <code v-text="tagExamples.init"></code>,
                                    <code v-text="tagExamples.text"></code>.
                                    Tags are detected automatically after upload.
                                    <q-btn flat dense no-caps color="primary" label="View all tags" icon="label"
                                           class="q-ml-sm" @click="showTagHelp = true" />
                                </q-banner>

                                <q-file
                                    v-model="newDocument"
                                    outlined
                                    label="Drop files here or click to upload"
                                    accept=".pdf,.doc,.docx"
                                    :max-file-size="50 * 1024 * 1024"
                                    @rejected="onFileRejected"
                                    @update:model-value="onUploadDocument"
                                    counter
                                    :loading="uploadingDocument"
                                >
                                    <template v-slot:prepend>
                                        <q-icon name="cloud_upload" />
                                    </template>
                                </q-file>
                                <div v-if="anyCloudImportEnabled" class="row q-gutter-sm q-mt-sm items-center">
                                    <span class="text-caption text-grey-7">Or import from</span>
                                    <q-btn
                                        v-if="dropboxImportEnabled"
                                        outline
                                        no-caps
                                        size="sm"
                                        color="primary"
                                        icon-right="img:https://www.dropbox.com/static/images/favicon.ico"
                                        label="Dropbox"
                                        :disable="uploadingDocument || googleDrivePicking || oneDrivePicking"
                                        :loading="dropboxPicking"
                                        @click="onPickFromDropbox"
                                    />
                                    <q-btn
                                        v-if="googleDriveImportEnabled"
                                        outline
                                        no-caps
                                        size="sm"
                                        color="primary"
                                        icon-right="img:https://ssl.gstatic.com/images/branding/product/1x/drive_2020q4_32dp.png"
                                        label="Google Drive"
                                        :disable="uploadingDocument || dropboxPicking || oneDrivePicking"
                                        :loading="googleDrivePicking"
                                        @click="onPickFromGoogleDrive"
                                    />
                                    <q-btn
                                        v-if="oneDriveImportEnabled"
                                        outline
                                        no-caps
                                        size="sm"
                                        color="primary"
                                        icon-right="img:https://onedrive.live.com/favicon.ico"
                                        label="OneDrive"
                                        :disable="uploadingDocument || dropboxPicking || googleDrivePicking"
                                        :loading="oneDrivePicking"
                                        @click="onPickFromOneDrive"
                                    />
                                </div>
                                <q-linear-progress
                                    v-if="uploadingDocument"
                                    :value="uploadProgress / 100"
                                    color="primary"
                                    size="8px"
                                    class="q-mt-sm"
                                />
                                <div class="text-caption text-grey-6 q-mt-sm">
                                    PDF or Word (.docx) files (max 50MB per file)
                                </div>
                            </q-card-section>

                        </q-card>

                        <q-card v-if="documents.length > 0" bordered class="q-mb-md">
                            <q-card-section class="bg-grey-1">
                                <div class="row items-center no-wrap">
                                    <q-icon name="folder_copy" size="sm" color="primary" class="q-mr-sm" />
                                    <div class="text-subtitle1 text-weight-bold">Uploaded Documents ({{ documents.length }})</div>
                                </div>
                            </q-card-section>
                            <q-separator />
                            <q-card-section>
                                <q-list bordered separator>
                                    <q-item v-for="doc in documents" :key="doc.id">
                                        <q-item-section avatar>
                                            <q-icon :name="getDocIcon(doc.fileExtension || doc.originalFileName)" :color="getDocIconColor(doc.fileExtension || doc.originalFileName)" />
                                        </q-item-section>
                                        <q-item-section>
                                            <q-item-label>
                                                {{ doc.name }}
                                                <q-badge v-if="doc.processingStatus === 'Failed'" color="negative" class="q-ml-sm">Failed</q-badge>
                                                <q-badge v-else-if="doc.processingStatus === 'Pending'" color="warning" text-color="dark" class="q-ml-sm">
                                                    <q-spinner-dots size="12px" class="q-mr-xs" /> Queued for Processing
                                                </q-badge>
                                                <q-badge v-else-if="doc.processingStatus === 'Converting'" color="info" class="q-ml-sm">
                                                    <q-spinner-dots size="12px" class="q-mr-xs" /> Converting
                                                </q-badge>
                                                <q-badge v-else-if="doc.processingStatus === 'DetectingTags'" color="info" class="q-ml-sm">
                                                    <q-spinner-dots size="12px" class="q-mr-xs" /> Detecting Tags
                                                </q-badge>
                                                <q-badge v-else-if="doc.processingStatus === 'Ready'" color="positive" class="q-ml-sm">Ready</q-badge>
                                            </q-item-label>
                                            <q-item-label caption>{{ formatFileSize(doc.fileSizeBytes) }}<span v-if="doc.pageCount"> - {{ doc.pageCount }} page(s)</span></q-item-label>
                                        </q-item-section>
                                        <q-item-section side>
                                            <q-btn flat round size="sm" icon="delete" color="negative" @click="onRemoveDocument(doc.id)" />
                                        </q-item-section>
                                    </q-item>
                                </q-list>
                            </q-card-section>
                        </q-card>
                        <q-stepper-navigation>
                            <q-btn @click="onProceedToStep3" color="primary" label="Continue" push :disable="!canProceedToStep3" />
                            <q-btn flat @click="step = 1" color="primary" label="Back" class="q-ml-sm" />
                        </q-stepper-navigation>
                    </q-step>

                    <!-- Step 3: Review Detected Tags -->
                    <q-step :name="3" title="Review Detected Tags" icon="sell" :done="step > 3 || allDocsScanned" :header-nav="step > 3">
                        <div class="text-h6 q-mb-xs">Review Detected Tags</div>
                        <div class="text-body2 text-grey-7 q-mb-lg">We scanned your documents for text tags that can be auto-converted to signing fields.</div>
                        <q-card bordered>
                            <q-card-section class="bg-grey-1">
                                <div class="row items-center no-wrap">
                                    <q-icon name="sell" size="sm" color="primary" class="q-mr-sm" />
                                    <div>
                                        <div class="text-subtitle1 text-weight-bold">Tag Detection Results</div>
                                        <div class="text-caption text-grey-7">Review auto-detected tags before proceeding.</div>
                                    </div>
                                </div>
                            </q-card-section>
                            <q-separator />
                            <q-card-section>
                                <div class="text-caption text-grey-7 q-mb-md">
                                    Tags are detected automatically after each document is uploaded and processed.
                                    The signer index numbers found in tags will be used to create signers in the next step.
                                    If a document failed processing, delete it and re-upload.
                                </div>

                                <!-- Processing notice -->
                                <q-banner v-if="hasProcessingDocs" class="bg-orange-1 q-mb-md" rounded>
                                    <template v-slot:avatar>
                                        <q-spinner-dots size="24px" color="orange" />
                                    </template>
                                    Some documents are still being processed. Tags will appear automatically when processing completes.
                                </q-banner>

                                <!-- Per-document detection -->
                                <q-list bordered separator class="q-mb-md">
                                    <q-item v-for="doc in documents" :key="doc.id">
                                        <q-item-section avatar>
                                            <q-icon :name="getDocIcon(doc.fileExtension || doc.originalFileName)" :color="getDocIconColor(doc.fileExtension || doc.originalFileName)" />
                                        </q-item-section>
                                        <q-item-section>
                                            <q-item-label>{{ doc.name }}</q-item-label>
                                            <q-item-label caption>
                                                <template v-if="doc.processingStatus !== 'Ready' && doc.processingStatus !== 'Failed'">
                                                    <q-spinner-dots size="14px" class="q-mr-xs" />
                                                    <span v-if="doc.renderProgress">
                                                        {{ doc.renderProgress.step === 'queued' ? 'Queued for rendering...' :
                                                           doc.renderProgress.step === 'downloading' ? 'Downloading...' :
                                                           doc.renderProgress.step === 'rendering' ? `Rendering ${doc.renderProgress.total} pages...` :
                                                           doc.renderProgress.step === 'uploading' ? `Uploading page ${doc.renderProgress.current}/${doc.renderProgress.total}` :
                                                           doc.renderProgress.step === 'complete' ? 'Finishing...' :
                                                           'Processing...' }}
                                                    </span>
                                                    <span v-else>
                                                        Processing ({{ doc.processingStatus === 'Pending' ? 'Queued' : doc.processingStatus || 'Queued' }})...
                                                    </span>
                                                </template>
                                                <template v-else-if="doc.processingStatus === 'Failed'">
                                                    <span class="text-negative text-weight-medium">Processing failed - delete and re-upload</span>
                                                </template>
                                                <template v-else-if="detectedTagsByDoc[doc.id] && detectedTagsByDoc[doc.id].detectionFailed">
                                                    <span class="text-negative text-weight-medium">
                                                        Could not scan for tags
                                                    </span>
                                                    <span class="text-grey-7">
                                                        - the document is usable for manual field placement, but we could not extract its text. Re-upload via Acrobat or another PDF tool if you embedded text tags in the document.
                                                    </span>
                                                </template>
                                                <template v-else-if="detectedTagsByDoc[doc.id]">
                                                    {{ detectedTagsByDoc[doc.id].tags?.length || 0 }} tag(s) detected
                                                    <span v-if="detectedTagsByDoc[doc.id].unrecognizedTagCount > 0" class="text-negative text-weight-medium">
                                                        - {{ detectedTagsByDoc[doc.id].unrecognizedTagCount }} tag(s) could not be positioned. Fix tags in document or remove it.
                                                    </span>
                                                </template>
                                                <template v-else>
                                                    Fetching detected tags...
                                                </template>
                                            </q-item-label>
                                        </q-item-section>
                                        <q-item-section side>
                                            <div class="row items-center q-gutter-xs">
                                                <q-spinner v-if="doc.processingStatus !== 'Ready' && doc.processingStatus !== 'Failed'"
                                                    size="20px" color="primary" />
                                                <q-badge v-if="detectedTagsByDoc[doc.id] && detectedTagsByDoc[doc.id].detectionFailed" color="warning" label="Scan failed" />
                                                <q-badge v-else-if="detectedTagsByDoc[doc.id]" color="positive" label="Scanned" />
                                            </div>
                                        </q-item-section>
                                    </q-item>
                                </q-list>

                                <!-- Detected Tags Detail (expandable per doc) -->
                                <template v-for="doc in documents" :key="'tags-' + doc.id">
                                    <q-expansion-item
                                        v-if="detectedTagsByDoc[doc.id] && detectedTagsByDoc[doc.id].tags?.length > 0"
                                        :label="doc.name + ' - Detected Tags'"
                                        icon="label"
                                        class="q-mb-sm"
                                        header-class="bg-grey-2"
                                    >
                                        <q-table
                                            :rows="detectedTagsByDoc[doc.id].tags"
                                            :columns="detectedTagColumns"
                                            row-key="rawTag"
                                            flat
                                            bordered
                                            dense
                                            :pagination="{ rowsPerPage: 20 }"
                                        >
                                            <template v-slot:body-cell-type="props">
                                                <q-td :props="props">
                                                    <q-chip size="sm" :icon="getAnnotationIcon(props.value)" :color="getAnnotationColor(props.value)" text-color="white">
                                                        {{ props.value }}
                                                    </q-chip>
                                                </q-td>
                                            </template>
                                            <template v-slot:body-cell-signerIndex="props">
                                                <q-td :props="props">
                                                    Signer {{ props.value }}
                                                </q-td>
                                            </template>
                                            <template v-slot:body-cell-required="props">
                                                <q-td :props="props">
                                                    <q-icon :name="props.value ? 'check' : 'remove'" :color="props.value ? 'positive' : 'grey'" />
                                                </q-td>
                                            </template>
                                        </q-table>
                                    </q-expansion-item>
                                </template>

                                <!-- Signer indices summary -->
                                <q-banner v-if="detectedSignerIndices.length > 0" class="bg-green-1 q-mt-md" rounded>
                                    <template v-slot:avatar>
                                        <q-icon name="group" color="positive" />
                                    </template>
                                    Detected <strong>{{ detectedSignerIndices.length }}</strong> unique signer index(es): {{ detectedSignerIndices.join(', ') }}.
                                    Placeholder signers will be created automatically in the next step - update their names and emails there.
                                </q-banner>
                            </q-card-section>
                        </q-card>
                        <q-stepper-navigation>
                            <q-btn @click="onProceedFromDetectTags" color="primary" label="Continue" push :disable="!allDocsScanned || hasProcessingDocs" :loading="loading" />
                            <q-btn flat @click="step = 2" color="primary" label="Back" class="q-ml-sm" />
                        </q-stepper-navigation>
                    </q-step>

                    <!-- Step 4: Signers & Review -->
                    <q-step :name="4" title="Signers & Review" icon="group" :header-nav="false">
                        <div class="text-h6 q-mb-xs">Add Signers &amp; Place Fields</div>
                        <div class="text-body2 text-grey-7 q-mb-lg">Add the people who need to sign, then drag fields onto the documents to define where they sign.</div>
                        <!-- Participants Card -->
                        <q-card bordered class="q-mb-md">
                            <q-card-section class="bg-grey-1">
                                <div class="row items-center no-wrap">
                                    <q-icon name="group" size="sm" color="primary" class="q-mr-sm" />
                                    <div class="col">
                                        <div class="text-subtitle1 text-weight-bold">Participants ({{ signers.length }})</div>
                                        <div class="text-caption text-grey-7">People who will sign, approve, or receive copies.</div>
                                    </div>
                                    <div class="col-auto q-gutter-x-sm">
                                        <q-btn v-if="signingGroupOptions.length > 0" outline color="primary" icon="group" label="CC Group" :disable="!signingGroupsAllowed" @click="openCcGroupDialog">
                                            <q-tooltip v-if="!signingGroupsAllowed">Signing groups require a Pro or Business plan</q-tooltip>
                                        </q-btn>
                                        <q-btn color="primary" push icon="add" label="Add Participant" @click="showAddSignerDialog = true" />
                                    </div>
                                </div>
                            </q-card-section>
                            <q-separator />
                            <q-card-section style="position: relative;">
                                <q-banner v-if="hasPlaceholderSigners" class="bg-warning text-white q-mb-md" rounded>
                                    <template v-slot:avatar>
                                        <q-icon name="warning" />
                                    </template>
                                    One or more signers still have placeholder information. Click the edit button to set their name and email address.
                                </q-banner>

                                <div v-if="signers.length === 0" class="text-center q-pa-lg">
                                    <q-icon name="person_add" size="48px" color="grey-5" />
                                    <div class="text-body1 text-grey-7 q-mt-md">No signers added yet</div>
                                    <q-btn color="primary" push label="Add First Participant" class="q-mt-md" @click="showAddSignerDialog = true" />
                                </div>

                                <template v-else>
                                <div v-for="phaseGroup in signerPhases" :key="phaseGroup.phase">
                                    <!-- Phase header (only when multiple phases present) -->
                                    <div v-if="signerPhases.length > 1" class="row items-center q-mt-sm q-mb-xs q-px-sm">
                                        <q-icon :name="phaseGroup.icon" :color="phaseGroup.color" size="xs" class="q-mr-xs" />
                                        <span class="text-caption text-weight-medium" :class="'text-' + phaseGroup.color">
                                            {{ phaseGroup.label }}
                                        </span>
                                        <span class="text-caption text-grey-6 q-ml-xs">- {{ phaseGroup.sublabel }}</span>
                                    </div>

                                    <!-- Enforce-order banner (only for Phase 2 signers) -->
                                    <q-banner v-if="phaseGroup.phase === 2 && showSignerReorder"
                                              class="bg-blue-1 q-mb-xs" rounded dense>
                                        <template v-slot:avatar>
                                            <q-icon name="north" color="primary" />
                                        </template>
                                        Signing order is enforced. Drag signers or use the arrows to adjust the order.
                                    </q-banner>

                                    <q-list bordered separator class="q-mb-xs" :class="signerPhases.length > 1 ? 'rounded-borders' : ''">
                                        <transition-group :name="phaseGroup.phase === 2 && showSignerReorder ? 'signer-swap' : ''"
                                                          :css="phaseGroup.phase === 2 && showSignerReorder">
                                        <q-item v-for="signer in phaseGroup.signers" :key="signer.id"
                                                clickable v-ripple @click="editSigner(signer)"
                                                :draggable="phaseGroup.phase === 2 && showSignerReorder"
                                                @dragstart="phaseGroup.phase === 2 && onDragStart(phase2Signers.indexOf(signer), $event)"
                                                @dragover.prevent="phaseGroup.phase === 2 && onDragOver(phase2Signers.indexOf(signer))"
                                                @dragenter.prevent
                                                @drop="phaseGroup.phase === 2 && onDrop(phase2Signers.indexOf(signer))"
                                                @dragend="onDragEnd"
                                                :class="{ 'signer-drag-over': phaseGroup.phase === 2 && dragOverIndex === phase2Signers.indexOf(signer), 'signer-dragging': phaseGroup.phase === 2 && dragIndex === phase2Signers.indexOf(signer) }">
                                            <q-item-section v-if="phaseGroup.phase === 2 && showSignerReorder"
                                                            side style="min-width: 24px; padding-right: 0;"
                                                            @click.stop>
                                                <q-icon name="drag_indicator" class="cursor-grab text-grey-6" />
                                            </q-item-section>
                                            <q-item-section avatar>
                                                <q-avatar :color="phaseGroup.color" text-color="white" size="36px">
                                                    {{ signer.routingOrder }}
                                                </q-avatar>
                                            </q-item-section>
                                            <q-item-section>
                                                <q-item-label>
                                                    {{ signer.name || '(No name)' }}
                                                    <q-badge v-if="signer.signingGroupId" color="blue-grey" class="q-ml-sm">
                                                        <q-icon name="group" size="12px" class="q-mr-xs" />
                                                        Group
                                                    </q-badge>
                                                    <q-badge v-if="signer.paymentAmount > 0" color="teal" class="q-ml-sm">
                                                        ${{ signer.paymentAmount.toFixed(2) }}
                                                    </q-badge>
                                                </q-item-label>
                                                <q-item-label caption>
                                                    <span v-if="signer.signingGroupId" class="text-blue-grey-6">
                                                        Any group member can sign
                                                    </span>
                                                    <span v-else-if="isSignerPlaceholder(signer)" class="text-warning text-weight-medium">
                                                        <q-icon name="error_outline" size="xs" class="q-mr-xs" />
                                                        Email required - click to set
                                                    </span>
                                                    <span v-else>{{ signer.email || '(No email)' }}</span>
                                                </q-item-label>
                                            </q-item-section>
                                            <q-item-section side>
                                                <div class="row items-center q-gutter-xs">
                                                    <template v-if="phaseGroup.phase === 2 && showSignerReorder">
                                                        <q-btn flat round dense size="sm" icon="arrow_upward"
                                                               :disable="phase2Signers.indexOf(signer) === 0 || loading"
                                                               @click.stop="moveSigner(phase2Signers.indexOf(signer), -1)" />
                                                        <q-btn flat round dense size="sm" icon="arrow_downward"
                                                               :disable="phase2Signers.indexOf(signer) === phase2Signers.length - 1 || loading"
                                                               @click.stop="moveSigner(phase2Signers.indexOf(signer), 1)" />
                                                    </template>
                                                    <q-chip size="sm" :color="getSignerTypeColor(signer.type)" outline>
                                                        {{ signer.roleName || signer.type }}
                                                    </q-chip>
                                                    <q-btn flat round size="sm" icon="delete" color="negative" :loading="removingSignerId === signer.id" @click.stop="onRemoveSigner(signer.id)" />
                                                </div>
                                            </q-item-section>
                                        </q-item>
                                        </transition-group>
                                    </q-list>
                                </div>
                                </template>
                                <q-inner-loading :showing="loading" label="Saving..." />
                            </q-card-section>

                        </q-card>

                        <!-- Field Editor Card -->
                        <q-card v-if="signers.length > 0" bordered class="q-mb-md">
                            <q-card-section class="bg-grey-1">
                                <div class="row items-center no-wrap">
                                    <q-icon name="edit_note" size="sm" color="primary" class="q-mr-sm" />
                                    <div class="col">
                                        <div class="text-subtitle1 text-weight-bold">Place Fields on Documents</div>
                                        <div class="text-caption text-grey-7">Drag fields from the toolbar onto the document pages.</div>
                                    </div>
                                    <q-space />
                                    <q-btn
                                        v-if="documentsWithPages.length > 0"
                                        outline dense no-caps
                                        icon="open_in_full"
                                        label="Expand Editor"
                                        color="primary"
                                        size="sm"
                                        @click="showEditorDialog = true"
                                    />
                                </div>
                            </q-card-section>
                            <q-separator />
                            <q-card-section class="q-px-sm q-py-sm">
                                <div v-if="documentsWithPages.length > 0" style="height: calc(100vh - 340px); min-height: 500px;">
                                    <FieldEditor
                                        :api-adapter="packageApiAdapter"
                                        :documents="documentsWithPages"
                                        :signers="fieldEditorSigners"
                                        :initialAnnotations="mappedAnnotations"
                                        @annotations-changed="onAnnotationsChanged"
                                    />
                                </div>
                                <div v-else-if="loadingPages" class="text-center q-pa-lg">
                                    <q-spinner size="40px" color="primary" />
                                    <div class="text-body2 text-grey-7 q-mt-sm">Loading document pages...</div>
                                </div>
                                <div v-else class="text-center q-pa-lg">
                                    <q-icon name="image" size="48px" color="grey-5" />
                                    <div class="text-body1 text-grey-7 q-mt-md">
                                        Page images not available
                                    </div>
                                    <div class="text-caption text-grey-6">
                                        Documents may still be processing. Try going back and waiting for processing to complete.
                                    </div>
                                </div>
                            </q-card-section>

                        </q-card>

                        <!-- Applied Fields Card -->
                        <q-card v-if="annotations.length > 0" bordered class="q-mb-md">
                            <q-card-section class="bg-grey-1">
                                <div class="row items-center no-wrap">
                                    <q-icon name="checklist" size="sm" color="primary" class="q-mr-sm" />
                                    <div class="text-subtitle1 text-weight-bold">Applied Fields ({{ annotations.length }})</div>
                                </div>
                            </q-card-section>
                            <q-separator />
                            <q-card-section class="q-py-sm">
                                <q-table
                                    :rows="annotations"
                                    :columns="annotationColumns"
                                    row-key="id"
                                    flat
                                    bordered
                                    dense
                                    :pagination="{ rowsPerPage: 10 }"
                                >
                                    <template v-slot:body-cell-document="props">
                                        <q-td :props="props" style="max-width: 180px" class="ellipsis">
                                            {{ getDocumentName(props.value) }}
                                            <q-tooltip>{{ getDocumentName(props.value) }}</q-tooltip>
                                        </q-td>
                                    </template>
                                    <template v-slot:body-cell-type="props">
                                        <q-td :props="props">
                                            <q-chip size="sm" :icon="getAnnotationIcon(props.value)" :color="getAnnotationColor(props.value)" text-color="white">
                                                {{ props.value }}
                                            </q-chip>
                                            <span v-if="props.row.label" class="text-grey-8 q-ml-sm">{{ props.row.label }}</span>
                                        </q-td>
                                    </template>
                                </q-table>
                            </q-card-section>
                        </q-card>

                        <!-- Pre-send Credit Impact Summary -->
                        <q-card
                            v-if="sendImpact && sendImpact.types && sendImpact.types.length > 0"
                            bordered
                            flat
                            class="q-mb-md q-mt-sm"
                            :class="hasRefillLargerThanUsual ? 'bg-orange-1' : 'bg-blue-1'"
                        >
                            <q-card-section class="q-py-sm">
                                <div class="row items-center no-wrap q-mb-xs">
                                    <q-icon
                                        :name="hasRefillLargerThanUsual ? 'warning_amber' : 'shield'"
                                        :color="hasRefillLargerThanUsual ? 'orange-9' : 'primary'"
                                        size="sm"
                                        class="q-mr-sm"
                                    />
                                    <div class="text-subtitle2 text-weight-bold">
                                        Authentication credits needed to send
                                    </div>
                                    <!-- Inline refresh indicator: the initial-load spinner
                                         lives in the v-else-if branch below (shown when no
                                         preview data yet). This one shows during a REFETCH
                                         after the signer roster changes in Step 4 so the
                                         user has visual confirmation the numbers are being
                                         recomputed instead of seeing stale data with no
                                         indication. -->
                                    <q-spinner
                                        v-if="sendImpactLoading"
                                        size="16px"
                                        color="primary"
                                        class="q-ml-sm"
                                    >
                                        <q-tooltip>Recalculating credit impact&hellip;</q-tooltip>
                                    </q-spinner>
                                </div>
                                <div
                                    v-for="t in sendImpact.types"
                                    :key="t.type"
                                    class="text-body2 q-mb-xs"
                                >
                                    <span class="text-weight-medium">{{ t.neededForPackage }}&nbsp;{{ formatCreditTypeLabel(t.type) }}</span>
                                    credit<span v-if="t.neededForPackage !== 1">s</span> -
                                    <span class="text-grey-8">
                                        you have {{ t.freeQuotaAvailable + t.prepaidAvailable }}
                                        ({{ t.freeQuotaAvailable }} free this month + {{ t.prepaidAvailable }} prepaid)
                                    </span>
                                    <template v-if="t.autoRefillPacks > 0">
                                        <br />
                                        <span :class="t.refillLargerThanUsual ? 'text-orange-9 text-weight-medium' : 'text-grey-8'">
                                            <q-icon name="autorenew" size="xs" class="q-mr-xs" />
                                            Auto-refill will purchase {{ t.autoRefillPacks }} {{ formatCreditTypeLabel(t.type) }}
                                            pack<span v-if="t.autoRefillPacks !== 1">s</span>
                                            for ${{ Number(t.autoRefillCost).toFixed(2) }}
                                            <template v-if="t.shortfall > 0">to cover the shortfall and maintain your refill threshold</template>
                                            <template v-else>to keep your balance above the refill threshold</template>.
                                            <span v-if="t.refillLargerThanUsual" class="q-ml-xs">
                                                (larger than your configured refill size)
                                            </span>
                                        </span>
                                    </template>
                                    <template v-else-if="t.shortfall > 0">
                                        <br />
                                        <span class="text-negative text-weight-medium">
                                            <q-icon name="error" size="xs" class="q-mr-xs" />
                                            Short {{ t.shortfall }} credit<span v-if="t.shortfall !== 1">s</span>.
                                            Purchase more in Billing or enable auto-refill before sending.
                                        </span>
                                    </template>
                                </div>
                                <!-- Show the auto-refill charge total only when send can
                                     actually proceed. If there's a blocking shortfall
                                     (a credit type short on supply with no auto-refill
                                     configured), the send aborts before any auto-refill
                                     fires - surface that clearly instead of a misleading
                                     "Sending will charge $X" line. -->
                                <div
                                    v-if="hasCreditShortfallBlock"
                                    class="text-body2 q-mt-sm text-weight-medium text-negative"
                                >
                                    <q-icon name="block" size="sm" class="q-mr-xs" />
                                    Cannot send until the shortfall above is resolved.
                                    No card will be charged until the package can complete its credit reservations.
                                    <template v-if="sendImpact.totalNewCharges > 0">
                                        <br />
                                        <span class="text-grey-8 text-weight-regular">
                                            <em>Once unblocked, this send would also trigger
                                            <strong>${{ Number(sendImpact.totalNewCharges).toFixed(2) }}</strong>
                                            in auto-refill charges.</em>
                                        </span>
                                    </template>
                                </div>
                                <div
                                    v-else-if="sendImpact.totalNewCharges > 0"
                                    class="text-body2 q-mt-sm text-weight-medium"
                                >
                                    <q-icon name="payments" size="sm" class="q-mr-xs" />
                                    Sending will charge <strong>${{ Number(sendImpact.totalNewCharges).toFixed(2) }}</strong>
                                    to your card for auto-refill.
                                </div>
                            </q-card-section>
                        </q-card>
                        <div v-else-if="sendImpactLoading" class="q-mb-md q-mt-sm text-body2 text-grey-7">
                            <q-spinner size="14px" color="primary" class="q-mr-xs" />
                            Checking authentication credit availability...
                        </div>

                        <!-- Validation Errors Inline -->
                        <q-banner v-if="validationErrors.length > 0" class="bg-negative text-white q-mb-md q-mt-sm" rounded>
                            <template v-slot:avatar>
                                <q-icon name="error" />
                            </template>
                            <div class="text-weight-bold">Package cannot be sent:</div>
                            <ul class="q-mb-none q-mt-xs" style="padding-left: 20px;">
                                <li v-for="(err, i) in validationErrors" :key="i">{{ err.message }}</li>
                            </ul>
                        </q-banner>

                        <q-stepper-navigation>
                            <!-- Scheduled edit: primary action is "Save Changes" (keeps schedule) -->
                            <q-btn
                                v-if="isScheduledEdit"
                                @click="saveScheduledChanges"
                                color="primary"
                                push
                                icon="save"
                                label="Save Changes"
                                :loading="sending"
                                :disable="!canSend"
                            >
                                <q-tooltip>Save changes and keep the existing schedule</q-tooltip>
                            </q-btn>
                            <q-btn
                                @click="onSendPackage"
                                :color="isScheduledEdit ? 'secondary' : 'primary'"
                                :flat="isScheduledEdit"
                                :push="!isScheduledEdit"
                                icon="send"
                                label="Send Now"
                                class="q-ml-sm"
                                :loading="sending"
                                :disable="!canSend || sending"
                            >
                                <q-tooltip v-if="isScheduledEdit">Cancel the schedule and send immediately</q-tooltip>
                                <q-tooltip v-else-if="sendDisabledReason">{{ sendDisabledReason }}</q-tooltip>
                            </q-btn>
                            <q-btn
                                v-if="!isScheduledEdit"
                                flat
                                color="secondary"
                                icon="schedule"
                                label="Schedule Send"
                                class="q-ml-sm"
                                :disable="!canSend"
                                @click="onScheduleSendClicked"
                            />
                            <q-btn
                                flat
                                @click="saveAsDraft"
                                color="primary"
                                :label="isScheduledEdit ? 'Cancel Schedule' : 'Save as Draft'"
                                class="q-ml-sm"
                            >
                                <q-tooltip v-if="isScheduledEdit">Cancel the schedule and revert to Draft</q-tooltip>
                            </q-btn>
                            <q-btn flat @click="step = 3" color="primary" label="Back" class="q-ml-sm" />
                        </q-stepper-navigation>
                    </q-step>
                </q-stepper>
            </div>
        </div>

        <!-- Text Tag Reference Dialog -->
        <q-dialog backdrop-filter="blur(4px) grayscale(50%)" v-model="showTagHelp" maximized transition-show="slide-up" transition-hide="slide-down">
            <q-card>
                <q-bar class="bg-primary text-white">
                    <div class="text-subtitle1 text-weight-bold">Text Tag Reference</div>
                    <q-space />
                    <q-btn dense flat icon="close" v-close-popup />
                </q-bar>
                <q-card-section class="scroll" style="max-height: calc(100vh - 50px);">
                    <div class="text-subtitle2 q-mb-sm">How Text Tags Work</div>
                    <p class="text-body2 text-grey-8">
                        Place text tags anywhere in your PDF or Word (.docx) document. When the document is processed,
                        each tag is replaced with an interactive field positioned at the tag's location. The signer index number
                        (1, 2, 3...) maps to signers in the order they are defined in Step 4.
                    </p>
                    <div class="text-subtitle2 q-mt-md q-mb-sm">Tag Format</div>
                    <p class="text-body2 text-grey-8">
                        <code class="text-primary" v-text="'{{type:signerIndex:required:label:options}}'"></code>
                    </p>
                    <q-list dense class="q-mb-md">
                        <q-item>
                            <q-item-section avatar style="min-width: 110px">
                                <code>type</code>
                            </q-item-section>
                            <q-item-section>Tag type (see table below)</q-item-section>
                        </q-item>
                        <q-item>
                            <q-item-section avatar style="min-width: 110px">
                                <code>signerIndex</code>
                            </q-item-section>
                            <q-item-section>Which signer (1, 2, 3...)</q-item-section>
                        </q-item>
                        <q-item>
                            <q-item-section avatar style="min-width: 110px">
                                <code>*</code>
                            </q-item-section>
                            <q-item-section>Required field (omit for optional)</q-item-section>
                        </q-item>
                        <q-item>
                            <q-item-section avatar style="min-width: 110px">
                                <code>label</code>
                            </q-item-section>
                            <q-item-section>Optional display label</q-item-section>
                        </q-item>
                        <q-item>
                            <q-item-section avatar style="min-width: 110px">
                                <code>options</code>
                            </q-item-section>
                            <q-item-section>Pipe-separated choices for dropdown/radio</q-item-section>
                        </q-item>
                    </q-list>

                    <div class="text-subtitle2 q-mt-md q-mb-sm">Available Tag Types</div>
                    <q-markup-table flat bordered dense separator="cell" class="q-mb-lg">
                        <thead>
                            <tr class="bg-grey-2">
                                <th class="text-left">Type</th>
                                <th class="text-left">Description</th>
                                <th class="text-left">Example</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>sig</code></td>
                                <td>Signature field</td>
                                <td><code v-text="tagExamples.sig"></code></td>
                            </tr>
                            <tr>
                                <td><code>init</code></td>
                                <td>Initials field</td>
                                <td><code v-text="tagExamples.init"></code></td>
                            </tr>
                            <tr>
                                <td><code>text</code></td>
                                <td>Free text input</td>
                                <td><code v-text="tagExamples.text"></code></td>
                            </tr>
                            <tr>
                                <td><code>name</code></td>
                                <td>Full name (auto-filled)</td>
                                <td><code v-text="tagExamples.name"></code></td>
                            </tr>
                            <tr>
                                <td><code>email</code></td>
                                <td>Email address (auto-filled)</td>
                                <td><code v-text="tagExamples.email"></code></td>
                            </tr>
                            <tr>
                                <td><code>date</code></td>
                                <td>Date picker</td>
                                <td><code v-text="tagExamples.date"></code></td>
                            </tr>
                            <tr>
                                <td><code>datesigned</code></td>
                                <td>Date signed (auto-filled)</td>
                                <td><code v-text="tagExamples.datesigned"></code></td>
                            </tr>
                            <tr>
                                <td><code>company</code></td>
                                <td>Company name (auto-filled)</td>
                                <td><code v-text="tagExamples.company"></code></td>
                            </tr>
                            <tr>
                                <td><code>title</code></td>
                                <td>Job title (auto-filled)</td>
                                <td><code v-text="tagExamples.title"></code></td>
                            </tr>
                            <tr>
                                <td><code>checkbox</code></td>
                                <td>Checkbox</td>
                                <td><code v-text="tagExamples.checkbox"></code></td>
                            </tr>
                            <tr>
                                <td><code>dropdown</code></td>
                                <td>Dropdown selection</td>
                                <td><code v-text="tagExamples.dropdown"></code></td>
                            </tr>
                            <tr>
                                <td><code>radio</code></td>
                                <td>Radio button group</td>
                                <td><code v-text="tagExamples.radio"></code></td>
                            </tr>
                        </tbody>
                    </q-markup-table>

                    <div class="text-subtitle2 q-mt-md q-mb-sm">Examples</div>
                    <q-markup-table flat bordered dense separator="cell">
                        <thead>
                            <tr class="bg-grey-2">
                                <th class="text-left">Tag</th>
                                <th class="text-left">Result</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code v-text="tagExamples.sig"></code></td>
                                <td>Required signature for signer 1</td>
                            </tr>
                            <tr>
                                <td><code v-text="tagExamples.init"></code></td>
                                <td>Required initials for signer 2</td>
                            </tr>
                            <tr>
                                <td><code v-text="tagExamples.textPhone"></code></td>
                                <td>Optional text field labeled "Phone" for signer 1</td>
                            </tr>
                            <tr>
                                <td><code v-text="tagExamples.dropdown"></code></td>
                                <td>Required dropdown with 3 options for signer 1</td>
                            </tr>
                            <tr>
                                <td><code v-text="tagExamples.datesigned"></code></td>
                                <td>Auto-filled signing date for signer 1</td>
                            </tr>
                        </tbody>
                    </q-markup-table>
                </q-card-section>
            </q-card>
        </q-dialog>

        <!-- Add/Edit Signer Dialog -->
        <q-dialog backdrop-filter="blur(4px) grayscale(50%)" v-model="showAddSignerDialog" persistent @show="ensureSigningGroupsLoaded">
            <q-card style="min-width: 400px; max-width: 500px; width: 100%;">
                <q-card-section>
                    <div class="text-h6">{{ editingSignerId ? 'Edit Signer' : 'Add Signer' }}</div>
                </q-card-section>

                <q-separator />

                <q-card-section class="scroll q-gutter-md" style="max-height: 65vh;">
                    <q-select
                        v-model="signerForm.type"
                        outlined
                        :options="signerTypeOptions"
                        emit-value
                        map-options
                        label="Recipient Type"
                        @update:model-value="onSignerTypeChanged"
                    >
                        <template v-slot:option="scope">
                            <q-item v-bind="scope.itemProps">
                                <q-item-section>
                                    <q-item-label>{{ scope.opt.label }}</q-item-label>
                                    <q-item-label caption>{{ scope.opt.description }}</q-item-label>
                                </q-item-section>
                            </q-item>
                        </template>
                    </q-select>

                    <!-- Assignment mode toggle: individual vs signing group -->
                    <q-btn-toggle
                        v-if="showSigningGroupToggle"
                        v-model="signerForm.assignmentMode"
                        spread
                        no-caps
                        rounded
                        toggle-color="primary"
                        :disable="!signingGroupsAllowed"
                        :options="[
                            { label: 'Individual', value: 'individual', icon: 'person' },
                            { label: 'Signing Group', value: 'group', icon: 'group' }
                        ]"
                        class="q-mb-xs"
                    >
                        <q-tooltip v-if="!signingGroupsAllowed">Signing groups require a Pro or Business plan</q-tooltip>
                    </q-btn-toggle>

                    <!-- Individual signer fields -->
                    <template v-if="signerForm.assignmentMode === 'individual'">
                        <q-input v-model="signerForm.name" outlined label="Name *" />
                        <q-input v-model="signerForm.email" outlined label="Email *" type="email" />
                    </template>

                    <!-- Signing group selection -->
                    <template v-else>
                        <q-select
                            v-model="signerForm.signingGroupId"
                            outlined
                            :options="signingGroupOptions"
                            emit-value
                            map-options
                            label="Signing Group *"
                            hint="Any member of the selected group can sign for this recipient"
                            :rules="[val => !!val || 'Signing group is required']"
                        />
                    </template>

                    <template v-if="signerForm.type === 'Signer' || signerForm.type === 'InPersonSigner'">
                        <div class="text-subtitle2 q-mb-xs">Authentication Methods</div>
                        <div class="text-caption text-grey-7 q-mb-sm">Select one or more methods. Multiple methods are verified sequentially.</div>
                        <div class="column q-gutter-xs q-mb-md">
                            <q-checkbox
                                v-for="opt in stackableAuthOptions"
                                :key="opt.value"
                                :model-value="signerForm.requiredAuthMethods.includes(opt.value)"
                                @update:model-value="toggleAuthMethod(opt.value, $event)"
                                :label="opt.label"
                                :disable="!isAuthMethodAllowed(opt.value)"
                                dense
                            >
                                <q-tooltip>{{ isAuthMethodAllowed(opt.value) ? opt.description : authMethodUpgradeHint(opt.value) }}</q-tooltip>
                            </q-checkbox>
                        </div>
                        <q-input
                            v-if="signerForm.requiredAuthMethods.includes('AccessCode')"
                            v-model="signerForm.accessCode"
                            ref="accessCodeInput"
                            outlined
                            :label="editingSignerId && !signerForm.accessCode ? 'Access Code (unchanged)' : 'Access Code *'"
                            :hint="editingSignerId && !signerForm.accessCode ? 'Leave blank to keep the existing code, or enter a new one to replace it. For security, the code is not displayed after saving.' : 'Share this code with the signer via a separate channel. For security, the code will not be displayed again after saving.'"
                            :placeholder="editingSignerId ? '(existing code hidden)' : ''"
                            :rules="[val => !!editingSignerId || (!!val && val.trim().length > 0) || 'An access code is required']"
                        >
                            <template v-slot:append>
                                <q-btn flat dense icon="auto_fix_high" size="sm" color="primary"
                                       @click="generateAccessCode" title="Generate a random code">
                                </q-btn>
                            </template>
                        </q-input>
                        <q-input
                            v-if="signerForm.requiredAuthMethods.includes('Sms')"
                            ref="phoneInput"
                            v-model="signerForm.phone"
                            outlined
                            label="Phone Number *"
                            mask="(###) ###-####"
                            hint="US phone number for SMS verification code"
                            :rules="[val => isValidSmsPhone(val) || 'A valid 10-digit US phone number is required']"
                        />
                        <q-select
                            v-if="signerForm.requiredAuthMethods.includes('Kba')"
                            v-model="signerForm.maxKbaAttempts"
                            outlined
                            :options="[1, 2, 3, 4, 5]"
                            label="Max KBA Attempts"
                            hint="Each attempt consumes 1 KBA credit. Credits are reserved on send and refunded if not consumed."
                        />
                    </template>

                    <!-- Payment Collection (inside scroll area) -->
                    <template v-if="signerForm.type === 'Signer' || signerForm.type === 'InPersonSigner'">
                    <q-separator class="q-my-md" />
                    <div :class="{ 'disabled-section': !paymentCollectionEnabled }">
                    <div class="text-caption text-grey-7 q-mb-sm">
                        <q-icon name="payments" size="14px" class="q-mr-xs" />
                        Payment Collection
                    </div>
                    <div class="row items-center q-gutter-md">
                        <q-toggle
                            :model-value="!!signerForm.paymentAmount"
                            label="Collect payment from this signer"
                            :disable="!paymentCollectionEnabled || ibtBlocksAdditionalPayer"
                            @update:model-value="val => signerForm.paymentAmount = val ? 1.00 : null"
                        >
                            <q-tooltip v-if="!paymentCollectionAllowed">
                                Upgrade your subscription to enable payment collection
                            </q-tooltip>
                            <q-tooltip v-else-if="taxElectionUndeclared">
                                Declare your sales-tax collection election in Payment Settings before collecting signer payments.
                            </q-tooltip>
                            <q-tooltip v-else-if="ibtBlocksAdditionalPayer">
                                Only one signer can collect payment when Instant Bank Payments is enabled.
                                Remove payment collection from the existing payer first.
                            </q-tooltip>
                        </q-toggle>
                    </div>
                    <q-input
                        v-if="signerForm.paymentAmount"
                        v-model.number="signerForm.paymentAmount"
                        outlined
                        dense
                        type="number"
                        label="Payment Amount"
                        prefix="$"
                        class="q-mt-sm"
                        style="max-width: 200px;"
                        hint="$0.50 – $5,000"
                        hide-bottom-space
                        :rules="[
                            val => val >= 0.50 || 'Minimum $0.50',
                            val => val <= 5000 || 'Maximum $5,000'
                        ]"
                        lazy-rules
                    />
                    <q-input
                        v-if="signerForm.paymentAmount"
                        v-model="signerForm.paymentDescription"
                        outlined
                        dense
                        label="Payment Description *"
                        class="q-mt-sm"
                        hint="What this payment is for (visible to signer)"
                        maxlength="200"
                        counter
                        :rules="[val => (val && val.trim().length > 0) || 'Payment description is required when collecting payment']"
                        lazy-rules
                    />
                    <q-select
                        v-if="signerForm.paymentAmount && taxCollectionEnabled"
                        v-model="signerForm.paymentTaxCodeId"
                        :options="taxCodeOptions"
                        option-value="id"
                        option-label="name"
                        emit-value
                        map-options
                        outlined
                        dense
                        use-input
                        clearable
                        input-debounce="150"
                        label="Tax classification (optional)"
                        hint="Classifies this signer's payment for sales-tax purposes. Leave blank to use your Payment Settings default."
                        :loading="loadingTaxCodes"
                        class="q-mt-sm"
                        @filter="onTaxCodeFilter"
                    >
                        <template #no-option>
                            <q-item>
                                <q-item-section class="text-grey-7">No tax codes match.</q-item-section>
                            </q-item>
                        </template>
                    </q-select>
                    </div>
                    </template>
                </q-card-section>

                <q-separator />

                <q-card-actions align="right" class="q-pa-md">
                    <q-btn flat label="Cancel" @click="closeSignerDialog" />
                    <q-btn color="primary" push :label="editingSignerId ? 'Update' : 'Add'" @click="onSaveSigner" :loading="loading" />
                </q-card-actions>
            </q-card>
        </q-dialog>

        <!-- Schedule Send Dialog -->
        <q-dialog backdrop-filter="blur(4px) grayscale(50%)" v-model="showScheduleDialog" persistent @show="startScheduleClock" @hide="stopScheduleClock">
            <q-card style="min-width: 400px">
                <q-card-section>
                    <div class="text-h6">Schedule Send</div>
                    <div class="text-caption text-grey-7">Choose when to send this package.</div>
                    <div class="text-caption text-grey-6 q-mt-xs">
                        Current time: <span class="text-weight-medium">{{ scheduleCurrentTime }}</span>
                        <span class="text-grey-5">({{ browserTimezone }})</span>
                    </div>
                </q-card-section>

                <q-card-section class="q-gutter-md">
                    <q-input v-model="scheduledDateDisplay" outlined label="Date *" readonly>
                        <template v-slot:append>
                            <q-icon name="calendar_today" class="cursor-pointer">
                                <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                                    <q-date v-model="scheduledDate" mask="YYYY-MM-DD" :options="futureDateOnly" />
                                </q-popup-proxy>
                            </q-icon>
                        </template>
                    </q-input>
                    <q-input v-model="scheduledTime" outlined :label="'Time (' + browserTimezone + ') *'" mask="##:## AA">
                        <template v-slot:append>
                            <q-icon name="schedule" class="cursor-pointer">
                                <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                                    <q-time v-model="scheduledTime" mask="hh:mm A" format12h />
                                </q-popup-proxy>
                            </q-icon>
                        </template>
                    </q-input>
                    <div v-if="scheduleTimeError" class="text-negative text-caption">
                        {{ scheduleTimeError }}
                    </div>
                </q-card-section>

                <q-card-actions align="right">
                    <q-btn flat label="Cancel" @click="showScheduleDialog = false" />
                    <q-btn
                        color="primary"
                        label="Schedule"
                        icon="schedule"
                        :disable="!scheduledDate || !scheduledTime || !!scheduleTimeError"
                        :loading="sending"
                        @click="onSchedulePackage"
                    />
                </q-card-actions>
            </q-card>
        </q-dialog>

        <!-- CC Entire Group Dialog -->
        <q-dialog backdrop-filter="blur(4px) grayscale(50%)" v-model="ccGroupDialogOpen" persistent>
            <q-card style="min-width: 400px;">
                <q-card-section>
                    <div class="text-h6">CC Entire Group</div>
                    <div class="text-caption text-grey-7">All members of the selected group will be added as Carbon Copy recipients.</div>
                </q-card-section>
                <q-card-section>
                    <q-select
                        v-model="ccGroupSelected"
                        outlined
                        :options="signingGroupOptions"
                        emit-value
                        map-options
                        label="Signing Group *"
                        :rules="[val => !!val || 'Please select a group']"
                    />
                </q-card-section>
                <q-card-actions align="right">
                    <q-btn flat label="Cancel" @click="ccGroupDialogOpen = false" />
                    <q-btn color="primary" push label="Add CC Recipients" :loading="ccGroupLoading" :disable="!ccGroupSelected" @click="onCcEntireGroup" />
                </q-card-actions>
            </q-card>
        </q-dialog>

        <!-- Expanded Field Editor Dialog -->
        <q-dialog backdrop-filter="blur(4px) grayscale(50%)" v-model="showEditorDialog" maximized transition-show="fade" transition-hide="fade">
            <q-card class="column no-wrap">
                <q-bar class="bg-primary text-white">
                    <q-icon name="design_services" />
                    <div class="text-subtitle1 text-weight-bold q-ml-sm">Field Editor - {{ basicInfo.name || 'Package' }}</div>
                    <q-space />
                    <q-btn dense flat icon="close" v-close-popup />
                </q-bar>
                <q-card-section class="col q-pa-xs" style="min-height: 0;">
                    <FieldEditor
                        v-if="showEditorDialog && documentsWithPages.length > 0"
                        :api-adapter="packageApiAdapter"
                        :documents="documentsWithPages"
                        :signers="fieldEditorSigners"
                        :initialAnnotations="mappedAnnotations"
                        @annotations-changed="onAnnotationsChanged"
                    />
                </q-card-section>
            </q-card>
        </q-dialog>

        <!-- Free-tier Per-Package Payment Dialog (reusable component).
             Two modes driven by consent-preview:
               • null → Stripe Elements (collect a one-time card)
               • { amount, cardBrand, cardLast4, ... } → saved-PM confirm
        -->
        <PerPackagePrepayDialog
            v-model="showPrePayDialog"
            :package-id="packageId"
            :consent-preview="overageConsentPreview"
            @success="onPrePaySuccess" />

        <!-- Server Validation Errors Dialog -->
        <q-dialog backdrop-filter="blur(4px) grayscale(50%)" :model-value="serverValidationErrors.length > 0" @update:model-value="clearError" persistent>
            <q-card style="min-width: 400px; max-width: 600px;">
                <q-card-section class="row items-center q-pb-none">
                    <q-icon name="error" color="negative" size="sm" class="q-mr-sm" />
                    <div class="text-h6">Package Cannot Be Sent</div>
                    <q-space />
                    <q-btn icon="close" flat round dense @click="clearError" />
                </q-card-section>
                <q-card-section>
                    <div class="text-body2 q-mb-sm">Please fix the following issues before sending:</div>
                    <q-list dense separator>
                        <q-item v-for="(err, i) in serverValidationErrors" :key="i">
                            <q-item-section avatar>
                                <q-icon name="cancel" color="negative" size="xs" />
                            </q-item-section>
                            <q-item-section>{{ err.message }}</q-item-section>
                        </q-item>
                    </q-list>
                </q-card-section>
                <q-card-actions align="right">
                    <q-btn flat label="Close" color="primary" @click="clearError" />
                </q-card-actions>
            </q-card>
        </q-dialog>

        <!-- Reusable Instant Bank Payments offer / reconcile dialog.
             Invoked by runPreSendGauntletAsync() via ref. Single source of truth
             for IBP UX across PackagesBuilder, PackageDetails, and PackagesIndex. -->
        <IbtOfferDialog ref="ibtDialog" />
    </AppLayout>
</template>

<script>
import AppLayout from '@components/common/AppLayout.vue';
import FieldEditor from '@components/FieldEditor/FieldEditor.vue';
import PerPackagePrepayDialog from '@components/Payment/PerPackagePrepayDialog.vue';
import IbtOfferDialog from '@components/Payment/IbtOfferDialog.vue';
import { usePackageWizardStore } from '@stores/packageWizard.js';
import { packages as packagesApi, annotations as annotationsApi, documents as documentsApi, signingGroups as signingGroupsApi } from '@services/signApi.js';
import { runPreSendGauntletAsync, classifyAndRunIbtDialogAsync } from '@services/preSendGauntlet.js';
import { mapState, mapWritableState, mapActions } from 'pinia';
import { isPlaceholderEmail } from '@services/packageValidation';
import { getTenantTimeZone } from '@services/dateFormat';
import { pickFromDropbox, isDropboxImportEnabled, pickFromGoogleDrive, isGoogleDriveImportEnabled, pickFromOneDrive, isOneDriveImportEnabled, prewarmOneDrive } from '@services/cloudImports.js';

const SIGNER_PHASES = {
    Editor:            { phase: 0, label: 'Editors',            icon: 'design_services',   color: 'cyan',    sublabel: 'Review and modify fields before signing begins' },
    Approver:          { phase: 1, label: 'Approvers',          icon: 'verified', color: 'orange',  sublabel: 'Must approve before signers receive the documents' },
    Signer:            { phase: 2, label: 'Signers',            icon: 'draw',           color: 'primary', sublabel: 'Complete required fields and signatures' },
    InPersonSigner:    { phase: 2, label: 'Signers',            icon: 'draw',           color: 'primary', sublabel: 'Complete required fields and signatures' },
    CertifiedDelivery: { phase: 3, label: 'Certified Delivery', icon: 'mark_email_read',    color: 'teal',    sublabel: 'Must acknowledge receipt after signing completes' },
    CarbonCopy:        { phase: 4, label: 'Carbon Copy',        icon: 'content_copy',   color: 'grey',    sublabel: 'Receive a copy - no action required' },
};

export default {
    name: 'PackagesBuilder',

    components: {
        AppLayout,
        FieldEditor,
        PerPackagePrepayDialog,
        IbtOfferDialog
    },

    data() {
        return {
            initialLoading: false,
            removingSignerId: null,
            newDocument: null,
            dropboxPicking: false,
            googleDrivePicking: false,
            oneDrivePicking: false,
            showTagHelp: false,
            showEditorDialog: false,
            showAddSignerDialog: false,
            showScheduleDialog: false,
            ccGroupDialogOpen: false,
            ccGroupSelected: null,
            ccGroupLoading: false,
            // (IBP dialog state now lives in <IbtOfferDialog ref="ibtDialog">.)
            scheduleClockTimer: null,
            scheduleCurrentTime: '',
            paymentCollectionAllowed: true, // assume allowed until subscription check
            // Tax-collection election state: 'Undeclared' blocks the payment toggle
            // because PackageService.ValidatePackageFullAsync rejects payment-required
            // sends when the tenant hasn't declared. Fetched alongside subscription info.
            taxElection: null,
            signingGroupsAllowed: true, // assume allowed until subscription check
            allowSmsAuth: true,
            allowKbaAuth: true,
            allowIdVerification: true,
            // Stripe Tax: catalogue + filter state for the per-package tax_code picker.
            taxCodes: [],
            filteredTaxCodes: [],
            loadingTaxCodes: false,
            // Free-tier per-package pre-payment dialog visibility. The dialog component
            // owns all the Stripe Elements state internally; this parent only tracks
            // whether to show it.
            showPrePayDialog: false,
            // Pre-send credit impact preview - lazy-loaded when the user reaches step 4
            // so the "Review & Send" screen can show "this send will use X KBA credits,
            // Y free + Z prepaid cover it; $W auto-refill will fire" above the Send button.
            sendImpact: null,
            sendImpactLoading: false,
            sendImpactError: null,
            editingSignerId: null,
            scheduledDate: null,
            scheduledTime: null,
            signerForm: {
                name: '',
                email: '',
                type: 'Signer',
                authMethod: 'Email',
                requiredAuthMethods: [],
                accessCode: '',
                phone: '',
                maxKbaAttempts: 2,
                signingGroupId: null,
                assignmentMode: 'individual',
                paymentAmount: null,
                paymentDescription: '',
                paymentTaxCodeId: null
            },
            signingGroupOptions: [],
            signingGroupsLoaded: false,
            tagExamples: {
                sig: '{{sig:1:*}}',
                init: '{{init:2:*}}',
                text: '{{text:1:*:Name}}',
                name: '{{name:1:*}}',
                email: '{{email:1:}}',
                date: '{{date:1:*:Effective Date}}',
                datesigned: '{{datesigned:1:*}}',
                company: '{{company:1:}}',
                title: '{{title:1:}}',
                checkbox: '{{checkbox:1:*:I agree}}',
                dropdown: '{{dropdown:1:*:State:CA|NY|TX}}',
                radio: '{{radio:1:*:Choice:Yes|No}}',
                textPhone: '{{text:1::Phone}}'
            },
            signerTypeOptions: [
                { label: 'Signer', value: 'Signer', description: 'Must sign, initial, or fill fields on the documents' },
                { label: 'Carbon Copy', value: 'CarbonCopy', description: 'Receives a copy of the completed documents - no action required' },
                { label: 'Certified Delivery', value: 'CertifiedDelivery', description: 'Must confirm receipt of the documents - tracked for compliance' },
                { label: 'Approver', value: 'Approver', description: 'Must approve the package before it proceeds - does not sign' },
                { label: 'Editor', value: 'Editor', description: 'Can modify recipients and fields before the package continues' }
            ],
            authMethodOptions: [
                { label: 'Email', value: 'Email', description: 'Signer verifies identity by clicking a unique link sent to their email' },
                { label: 'Access Code', value: 'AccessCode', description: 'Signer must enter a code you share with them out-of-band before signing' },
                { label: 'SMS', value: 'Sms', description: 'A one-time passcode is sent to the signer\'s phone via text message (consumes SMS credits)' },
                { label: 'KBA', value: 'Kba', description: 'Signer answers knowledge-based questions generated from public records (consumes KBA credits)' },
                { label: 'ID Verification', value: 'IdVerification', description: 'Signer verifies identity with a government-issued photo ID (consumes IDV credits)' }
            ],
            stackableAuthOptions: [
                { label: 'Access Code', value: 'AccessCode', description: 'Signer must enter a shared code (no credit cost)' },
                { label: 'SMS Verification', value: 'Sms', description: 'One-time passcode via text message (consumes SMS credit)' },
                { label: 'Knowledge-Based Auth (KBA)', value: 'Kba', description: 'Identity quiz from public records (consumes KBA credit)' },
                { label: 'ID Verification (Persona)', value: 'IdVerification', description: 'Government photo ID + selfie match (consumes IDV credit)' }
            ],
            detectedTagColumns: [
                { name: 'rawTag', label: 'Tag', field: 'rawTag', align: 'left' },
                { name: 'type', label: 'Type', field: 'type', align: 'left' },
                { name: 'signerIndex', label: 'Signer', field: 'signerIndex', align: 'left' },
                { name: 'pageNumber', label: 'Page', field: 'pageNumber', align: 'center' },
                { name: 'required', label: 'Required', field: 'required', align: 'center' },
                { name: 'label', label: 'Label', field: 'label', align: 'left', format: v => v || '-' }
            ],
            annotationColumns: [
                { name: 'document', label: 'Document', field: 'documentId', align: 'left', style: 'max-width: 180px', classes: 'ellipsis' },
                { name: 'type', label: 'Type', field: 'type', align: 'left' },
                { name: 'signer', label: 'Signer', field: 'signerIndex', align: 'left', format: (v) => `Signer ${v ?? 1}` },
                { name: 'pageNumber', label: 'Page', field: 'pageNumber', align: 'center' },
                { name: 'required', label: 'Required', field: 'required', align: 'center', format: v => v ? 'Yes' : 'No' }
            ],
            dragIndex: null,
            dragOverIndex: null
        };
    },

    computed: {
        ...mapWritableState(usePackageWizardStore, [
            'step'
        ]),
        ...mapState(usePackageWizardStore, [
            'mode',
            'packageId',
            'basicInfo',
            'documents',
            'signers',
            'annotations',
            'detectedTagsByDoc',
            'documentsWithPages',
            'loading',
            'uploadingDocument',
            'uploadProgress',
            'sending',
            'loadingPages',
            'error',
            'isEditing',
            'isScheduledEdit',
            'canProceedToStep2',
            'canProceedToStep3',
            'canProceedToStep4',
            'allDocsScanned',
            'detectedSignerIndices',
            'docsWithNoTags',
            'hasProcessingDocs',
            'allDocsReady',
            'hasPlaceholderSigners',
            'sendDisabledReason',
            'canSend',
            'stepBlockReason',
            'expirationDateError',
            'validationResult',
            'serverValidationErrors',
            'overageConsentPreview'
        ]),

        pageTitle() {
            if (this.isEditing) return 'Edit Package';
            if (this.mode === 'template') return 'Create Package from Template';
            return 'Create Package';
        },

        dropboxImportEnabled() {
            return isDropboxImportEnabled();
        },

        googleDriveImportEnabled() {
            return isGoogleDriveImportEnabled();
        },

        oneDriveImportEnabled() {
            return isOneDriveImportEnabled();
        },

        anyCloudImportEnabled() {
            return this.dropboxImportEnabled || this.googleDriveImportEnabled || this.oneDriveImportEnabled;
        },

        browserTimezone() {
            return getTenantTimeZone() || 'UTC';
        },

        scheduledDateDisplay() {
            if (!this.scheduledDate) return '';
            // Convert YYYY-MM-DD to MM-DD-YYYY
            const [y, m, d] = this.scheduledDate.split('-');
            return `${m}-${d}-${y}`;
        },

        scheduleTimeError() {
            if (!this.scheduledDate || !this.scheduledTime) return null;
            const time24 = this._convertTo24h(this.scheduledTime);
            if (!time24) return null;
            const tz = getTenantTimeZone() || 'UTC';
            const nowInTz = new Date().toLocaleString('en-US', { timeZone: tz });
            const nowLocal = new Date(nowInTz);
            const selectedLocal = new Date(`${this.scheduledDate}T${time24}:00`);
            if (selectedLocal <= nowLocal) {
                return 'Scheduled time must be in the future.';
            }
            // Prevent scheduling on or after the expiration date
            const expDate = this.basicInfo?.expirationDate;
            if (expDate && this.scheduledDate > expDate) {
                return 'Scheduled send must be before the expiration date.';
            }
            return null;
        },

        validationErrors() {
            return this.validationResult?.errors || [];
        },

        hasRefillLargerThanUsual() {
            return !!this.sendImpact?.types?.some(t => t.refillLargerThanUsual);
        },

        // True when at least one credit type has a shortfall the system can't
        // close on its own (no auto-refill configured for that type). The
        // package cannot be sent in this state - used to (1) suppress the
        // "Sending will charge $X" line, which misleads users into thinking
        // the send is going through, and (2) drive the explicit "cannot send"
        // alert at the top of the card.
        hasCreditShortfallBlock() {
            return !!this.sendImpact?.types?.some(t => t.shortfall > 0 && t.autoRefillPacks === 0);
        },

        // Hash of just the credit-affecting fields across all signers. Watching this
        // (vs deep-watching the whole signers array) lets us re-fetch the send-impact
        // preview the moment a user adds, edits, or removes a signer in step 4 - which
        // is when ALL signer mutations actually happen, since steps 1-3 are pre-signer.
        // Includes signer id so a remove+add of a signer with the same auth method
        // still re-fires (id changes).
        creditRelevantSignerSignature() {
            return (this.signers || []).map(s => {
                const required = Array.isArray(s.requiredAuthMethods) ? s.requiredAuthMethods.join(',') : '';
                return `${s.id}:${s.authMethod || ''}:${s.maxKbaAttempts || 0}:${required}`;
            }).join('|');
        },

        // Whether to show the individual/group toggle in the signer dialog.
        // Signing groups apply to all action-taking roles - only CarbonCopy is excluded
        // (receive-only, no action to delegate). The backend enforces the same boundary.
        showSigningGroupToggle() {
            return this.signerForm.type !== 'CarbonCopy' && this.signingGroupOptions.length > 0;
        },

        // Signers that can have fields placed on them (excludes CC, CD, Editor)
        // Each signer gets a stable colorIndex based on creation order so colors
        // don't shift when signers are added or reordered.
        fieldEditorSigners() {
            const actionableTypes = ['Signer'];
            const filtered = this.signers.filter(s => actionableTypes.includes(s.type));
            const byCreation = [...filtered].sort((a, b) =>
                new Date(a.createdOn) - new Date(b.createdOn) || (a.id < b.id ? -1 : 1));
            const colorMap = {};
            byCreation.forEach((s, idx) => { colorMap[s.routingOrder] = idx; });
            return filtered.map(s => ({ ...s, colorIndex: colorMap[s.routingOrder] ?? 0 }));
        },

        // Group signers by pipeline phase for visual rendering
        signerPhases() {
            if (this.signers.length === 0) return [];
            const phaseMap = {};
            this.signers.forEach(signer => {
                const meta = SIGNER_PHASES[signer.type] || SIGNER_PHASES.Signer;
                if (!phaseMap[meta.phase]) {
                    phaseMap[meta.phase] = { ...meta, signers: [] };
                }
                phaseMap[meta.phase].signers.push(signer);
            });
            return Object.keys(phaseMap)
                .sort((a, b) => a - b)
                .map(k => phaseMap[k]);
        },

        // Whether drag/reorder controls should show (Phase 2 signers only, with enforced order)
        phase2Signers() {
            return this.signers.filter(s => {
                const meta = SIGNER_PHASES[s.type] || SIGNER_PHASES.Signer;
                return meta.phase === 2;
            });
        },

        showSignerReorder() {
            return this.basicInfo.enforceSigningOrder && this.phase2Signers.length > 1;
        },

        packageApiAdapter() {
            const pkgGuid = this.packageId;
            const filteredSigners = this.fieldEditorSigners;
            return {
                async create(data) {
                    // Convert FieldEditor's 0-based array index to signer's routingOrder for the API
                    const signer = filteredSigners[data.signerIndex];
                    const apiData = { ...data, signerIndex: signer?.routingOrder ?? 1 };
                    const r = await annotationsApi.create(pkgGuid, apiData);
                    return r.data;
                },
                async update(id, data) {
                    const updateData = { ...data };
                    if (data.signerIndex !== undefined) {
                        const signer = filteredSigners[data.signerIndex];
                        updateData.signerIndex = signer?.routingOrder ?? data.signerIndex;
                    }
                    await annotationsApi.update(pkgGuid, id, updateData);
                },
                async delete(id) {
                    await annotationsApi.delete(pkgGuid, id);
                }
            };
        },

        // Convert store annotations (routingOrder-based signerIndex) to FieldEditor format (0-based array index)
        mappedAnnotations() {
            const routingOrderToIndex = {};
            const routingOrderToColorIndex = {};
            this.fieldEditorSigners.forEach((s, idx) => {
                routingOrderToIndex[s.routingOrder] = idx;
                routingOrderToColorIndex[s.routingOrder] = s.colorIndex;
            });
            return this.annotations
                .filter(ann => routingOrderToIndex[ann.signerIndex] !== undefined)
                .map(ann => ({
                    id: ann.id,
                    documentId: ann.documentId,
                    type: ann.type,
                    signerIndex: routingOrderToIndex[ann.signerIndex] ?? 0,
                    colorIndex: routingOrderToColorIndex[ann.signerIndex] ?? 0,
                    pageNumber: ann.pageNumber,
                    x: ann.x,
                    y: ann.y,
                    width: ann.width,
                    height: ann.height,
                    required: ann.required,
                    mustBeChecked: ann.mustBeChecked,
                    label: ann.label,
                    defaultValue: ann.defaultValue,
                    options: ann.options,
                    group: ann.group,
                })).map(a => { if (a.type === 'Dropdown') console.log('[PackagesBuilder.mappedAnnotations] Dropdown id=', a.id, 'options=', JSON.stringify(a.options)); return a; });
        },

        taxCodeOptions() {
            return this.filteredTaxCodes.length > 0 ? this.filteredTaxCodes : this.taxCodes;
        },

        taxElectionUndeclared() {
            return this.taxElection === 'Undeclared';
        },

        // Tax classification picker is shown only when the tenant has
        // actually opted into Stripe Tax (election='Enabled'). 'NotRequired'
        // tenants skip tax calculation entirely and have no use for a tax
        // code on the signer; 'Undeclared' tenants can't even reach this UI
        // because the payment toggle is gated above.
        taxCollectionEnabled() {
            return this.taxElection === 'Enabled';
        },

        // Combined gate for the signer-payment toggle. Payment collection is
        // enabled when the plan allows AND the tenant has declared their
        // tax-collection election. The tooltip in the template chooses which
        // reason to surface (plan upgrade vs election).
        paymentCollectionEnabled() {
            return this.paymentCollectionAllowed && !this.taxElectionUndeclared;
        },

        // True when the Add Signer / Edit Signer dialog should disable the
        // "Collect payment" toggle because Instant Bank Payments is on AND
        // another (different) signer already collects payment. Allows the
        // current payer to keep their toggle on (so editing them works); only
        // blocks promoting a SECOND signer to payer. Matches the §6A spec.
        ibtBlocksAdditionalPayer() {
            const store = usePackageWizardStore();
            if (!store.basicInfo.enableInstantBankTransfer) return false;
            const signers = store.signers || [];
            // Existing payers (other than the one being edited).
            const existingPayer = signers.find(s =>
                s.paymentAmount && s.paymentAmount > 0
                && s.signerId !== this.signerForm.signerId);
            // If a payer exists and current dialog's signer doesn't already
            // have payment on, the toggle is blocked.
            return !!existingPayer && !this.signerForm.paymentAmount;
        }
    },

    methods: {
        ...mapActions(usePackageWizardStore, [
            'initFromScratch',
            'initFromTemplate',
            'loadPackage',
            'saveBasicInfo',
            'uploadDocument',
            'removeDocument',
            'addSigner',
            'updateSigner',
            'removeSigner',
            'applyDetectedTags',
            'loadDocumentPages',
            'sendPackage',
            'schedulePackage',
            'nextStep',
            'goToStep',
            'clearError',
            'ensureTagsLoaded',
            'fetchDetectedTags',
            'reorderSigners',
            'getDocumentRemovalImpact',
            '_removeOrphanedSigners',
            '_stopPolling'
        ]),

        async onNextStep() {
            const success = await this.nextStep();
            if (!success && this.error) {
                this.$q.notify({ type: 'negative', message: this.error });
                this.clearError();
            }
        },

        // q-date passes the candidate date as "YYYY/MM/DD"; we compare against
        // "today" in the tenant TZ (same wall clock the server uses for the
        // expiration check) to grey out any date before today in the picker.
        // The inline q-input error catches typed past dates the picker can't
        // intercept, and the Continue button is also gated via the store.
        expirationDatePickerOptions(dateStr) {
            const tz = getTenantTimeZone() || 'UTC';
            const today = new Intl.DateTimeFormat('en-CA', {
                timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit'
            }).format(new Date());
            return dateStr.replace(/\//g, '-') >= today;
        },

        formatCreditTypeLabel(type) {
            switch (type) {
                case 'IDV': return 'ID Verification';
                case 'KBA': return 'KBA';
                case 'SMS': return 'SMS';
                default: return type;
            }
        },

        /**
         * Fetches the pre-send credit impact preview so the Review & Send step can show
         * "this package will use X KBA credits; $Y auto-refill will fire" above the Send
         * button. Read-only - no reservations written, no Stripe charges made.
         */
        async loadSendImpact() {
            if (!this.packageId) return;
            this.sendImpactLoading = true;
            this.sendImpactError = null;
            try {
                const res = await packagesApi.getSendImpact(this.packageId);
                this.sendImpact = res.data;
            } catch (err) {
                // Non-fatal: the Send button still works without the preview. Log so
                // we can surface the error in DevTools but don't block the UI.
                console.error('[PackagesBuilder] Failed to load send-impact preview:', err);
                this.sendImpactError = err.response?.data?.message || err.message || 'Could not load credit preview';
                this.sendImpact = null;
            } finally {
                this.sendImpactLoading = false;
            }
        },

        onProceedToStep3() {
            // The `watch(step)` handler (immediate: true) calls ensureTagsLoaded()
            // whenever step becomes 3, regardless of how the change was triggered
            // (button, watcher, back-nav from step 4, restored draft). That one
            // trigger is sufficient. A second direct call here previously burned
            // 2/5 retry budget on every step-3 entry. Don't reintroduce it.
            this.step = 3;
        },

        onFileRejected(entries) {
            const entry = entries?.[0];
            if (entry?.failedPropValidation === 'max-file-size') {
                this.$q.notify({ type: 'warning', message: 'File exceeds maximum size of 50 MB' });
            } else if (entry?.failedPropValidation === 'accept') {
                this.$q.notify({ type: 'warning', message: 'Only PDF, DOC, and DOCX files are accepted' });
            }
        },

        async onUploadDocument(file) {
            if (!file) return;
            const doc = await this.uploadDocument(file);
            this.newDocument = null;
            if (!doc && this.error) {
                this.$q.notify({ type: 'negative', message: this.error });
                this.clearError();
            }
        },

        async onPickFromDropbox() {
            this.dropboxPicking = true;
            let file;
            try {
                file = await pickFromDropbox();
            } catch (err) {
                if (err?.message !== 'cancelled') {
                    this.$q.notify({ type: 'negative', message: err.message || 'Dropbox import failed' });
                }
                return;
            } finally {
                this.dropboxPicking = false;
            }
            await this.onUploadDocument(file);
        },

        async onPickFromGoogleDrive() {
            this.googleDrivePicking = true;
            let file;
            try {
                file = await pickFromGoogleDrive();
            } catch (err) {
                if (err?.message !== 'cancelled') {
                    this.$q.notify({ type: 'negative', message: err.message || 'Google Drive import failed' });
                }
                return;
            } finally {
                this.googleDrivePicking = false;
            }
            await this.onUploadDocument(file);
        },

        async onPickFromOneDrive() {
            this.oneDrivePicking = true;
            let file;
            try {
                file = await pickFromOneDrive();
            } catch (err) {
                if (err?.message !== 'cancelled') {
                    this.$q.notify({ type: 'negative', message: err.message || 'OneDrive import failed' });
                }
                return;
            } finally {
                this.oneDrivePicking = false;
            }
            await this.onUploadDocument(file);
        },

        async onRemoveDocument(documentId) {
            const doc = this.documents.find(d => d.id === documentId);

            // Pre-check: fetch orphaned signers from server
            this.$q.loading.show({ message: 'Checking document dependencies...' });
            let impact;
            try {
                impact = await this.getDocumentRemovalImpact(documentId);
            } finally {
                this.$q.loading.hide();
            }
            const orphans = impact?.orphanedSigners || [];
            const hasOrphans = orphans.length > 0;

            const docName = doc?.name || 'this document';
            let message = `Remove "<b>${docName}</b>" from the package? This will also delete any detected tags and annotations for this document.`;
            if (hasOrphans) {
                const signerItems = orphans.map(s => `<li>${s.name} (${s.email})</li>`).join('');
                message += `<br><br>The following signer(s) will also be removed because they only have fields on this document:<ul>${signerItems}</ul>`;
            }

            this.$q.dialog({
                title: hasOrphans ? 'Remove Document & Orphaned Signers' : 'Remove Document',
                message,
                html: true,
                cancel: true,
                persistent: true
            }).onOk(async () => {
                this.$q.loading.show({ message: hasOrphans ? 'Removing document and orphaned signers...' : 'Removing document...' });
                try {
                    const success = await this.removeDocument(documentId);
                    if (success && hasOrphans) {
                        const failCount = await this._removeOrphanedSigners(orphans);
                        if (failCount > 0) {
                            this.$q.notify({ type: 'warning', message: `Document removed, but ${failCount} orphaned signer(s) could not be removed. You may need to remove them manually.` });
                        }
                    }
                    if (!success && this.error) {
                        this.$q.notify({ type: 'negative', message: this.error });
                        this.clearError();
                    }
                } finally {
                    this.$q.loading.hide();
                }
            });
        },

        onProceedFromDetectTags() {
            const proceed = async () => {
                const store = usePackageWizardStore();
                store.loading = true;

                try {
                    // Load document pages - needed for FieldEditor
                    await this.loadDocumentPages();

                    // Apply detected tags as real annotations (idempotent - skips docs that already have annotations)
                    await this.applyDetectedTags();

                    // Pre-load signing groups so CC Group button is visible immediately
                    await this.ensureSigningGroupsLoaded();
                } catch (err) {
                    console.error('[PackagesBuilder] Failed to prepare editor:', err);
                    this.$q.notify({ type: 'negative', message: err.displayMessage || 'Failed to prepare fields. Please try again.' });
                } finally {
                    store.loading = false;
                }

                this.step = 4;
            };

            if (this.docsWithNoTags.length > 0) {
                const docList = this.docsWithNoTags.map(d => `<li>${d.name || 'Unnamed'}</li>`).join('');
                this.$q.dialog({
                    title: 'No Embedded Tags Found',
                    message: `<p>The following document(s) do not contain embedded text tags:</p><ul>${docList}</ul><p>This is normal if you didn't embed tags in your document. Signing fields can be placed manually in the next step.</p><p>If you expected tags to be detected, verify your document uses the correct tag format.</p>`,
                    html: true,
                    ok: { label: 'Continue', flat: true },
                    cancel: { label: 'Go Back', flat: true },
                    persistent: true
                }).onOk(() => proceed());
            } else {
                proceed();
            }
        },

        async onSaveSigner() {
            const isGroupMode = this.signerForm.assignmentMode === 'group';

            if (isGroupMode) {
                if (!this.signerForm.signingGroupId) {
                    this.$q.notify({ type: 'warning', message: 'Please select a signing group' });
                    return;
                }
                // Auto-fill name from group label, use placeholder email
                const group = this.signingGroupOptions.find(g => g.value === this.signerForm.signingGroupId);
                this.signerForm.name = group ? group.label.replace(/\s*\(\d+ members?\)$/, '') : 'Signing Group';
                this.signerForm.email = `group-${this.signerForm.signingGroupId}@signing-group.local`;
            } else {
                if (!this.signerForm.name || !this.signerForm.email) {
                    this.$q.notify({ type: 'warning', message: 'Name and email are required' });
                    return;
                }
                // Clear signing group when in individual mode
                this.signerForm.signingGroupId = null;
            }

            // Validate SMS phone number (single method or in stack)
            const hasSms = this.signerForm.requiredAuthMethods.includes('Sms') || this.signerForm.authMethod === 'Sms';
            if (hasSms && !this.isValidSmsPhone(this.signerForm.phone)) {
                this.$refs.phoneInput?.validate();
                this.$q.notify({ type: 'warning', message: 'A valid 10-digit phone number is required for SMS authentication' });
                return;
            }

            // Validate access code (single method or in stack) - skip on edit if not changed (existing code preserved)
            const hasAccessCode = this.signerForm.requiredAuthMethods.includes('AccessCode') || this.signerForm.authMethod === 'AccessCode';
            if (hasAccessCode && !this.signerForm.accessCode?.trim() && !this.editingSignerId) {
                this.$refs.accessCodeInput?.validate();
                this.$q.notify({ type: 'warning', message: 'An access code is required for Access Code authentication' });
                return;
            }

            // Payment description is required when collecting payment - the signer
            // sees it on the checkout page and needs to understand what they're
            // being charged for.
            if (this.signerForm.paymentAmount && !this.signerForm.paymentDescription?.trim()) {
                this.$q.notify({ type: 'warning', message: 'Payment description is required when collecting payment' });
                return;
            }

            // Strip assignmentMode before sending to API
            const { assignmentMode, ...apiData } = this.signerForm;

            // PATCH semantics on the update endpoint: null = "no change",
            // not "clear." The form binds nullable fields directly, so a
            // user clearing the dropdown / toggling off payment / switching
            // from group to individual all produce null in apiData - which
            // the server would silently ignore. Translate each "clear"
            // intent to the sentinel its API path recognizes:
            //   - PaymentAmount: 0 → API sets DB amount to null + IsPayer=false
            //   - PaymentTaxCodeId: "" → API clears the column
            //   - SigningGroupId: Guid.Empty → API clears the FK
            // Only translate on edit; on create, null fields are correctly
            // interpreted as "not set."
            if (this.editingSignerId) {
                if (apiData.paymentAmount == null) apiData.paymentAmount = 0;
                if (apiData.paymentTaxCodeId == null) apiData.paymentTaxCodeId = '';
                if (assignmentMode !== 'group' && apiData.signingGroupId == null) {
                    apiData.signingGroupId = '00000000-0000-0000-0000-000000000000';
                }
            }

            let success;
            if (this.editingSignerId) {
                success = await this.updateSigner(this.editingSignerId, apiData);
            } else {
                success = await this.addSigner(apiData);
            }

            if (success) {
                this.closeSignerDialog();
                // Auto-reorder by phase so the signer lands in the correct phase group
                await this.reorderSignersByPhase();
                // Explicit refresh of the credit-impact preview. The
                // creditRelevantSignerSignature watcher should also fire here,
                // but Pinia + array-index-replacement reactivity has been
                // unreliable for this code path (observed: signer auth-method
                // edit not refreshing the preview until manual page reload).
                // Calling it directly makes the refresh deterministic; the
                // watcher is now the safety net rather than the trigger.
                if (this.step === 4 && this.packageId) {
                    this.loadSendImpact();
                }
            } else if (this.error) {
                this.$q.notify({ type: 'negative', message: this.error });
                this.clearError();
            }
        },

        async onRemoveSigner(signerId) {
            const signer = this.signers.find(s => s.id === signerId);
            if (!signer) return;

            // Calculate impact from local store data
            const affectedAnnotations = this.annotations.filter(
                a => a.signerIndex === signer.routingOrder
            );
            const affectedDocIds = [...new Set(affectedAnnotations.map(a => a.documentId))];
            const affectedDocNames = affectedDocIds.map(docId => {
                const doc = this.documents.find(d => d.id === docId);
                return doc?.name || 'Unknown document';
            });

            const signerLabel = signer.name || '(No name)';
            const signerEmail = signer.email ? ` (${signer.email})` : '';
            const hasAnnotations = affectedAnnotations.length > 0;

            let message = `Remove signer "<b>${signerLabel}</b>"${signerEmail}?`;
            if (hasAnnotations) {
                const docItems = affectedDocNames.map(n => `<li>${n}</li>`).join('');
                message += `<br><br>This will also delete <b>${affectedAnnotations.length}</b> field(s) on the following document(s):<ul>${docItems}</ul>`;
            }

            this.$q.dialog({
                title: hasAnnotations ? 'Remove Signer & Fields' : 'Remove Signer',
                message,
                html: true,
                cancel: true,
                persistent: true
            }).onOk(async () => {
                this.removingSignerId = signerId;
                try {
                    const success = await this.removeSigner(signerId);
                    if (!success && this.error) {
                        this.$q.notify({ type: 'negative', message: this.error });
                        this.clearError();
                    } else if (success && this.step === 4 && this.packageId) {
                        // Refresh credit-impact preview now that a signer is gone.
                        // Mirrors the explicit refresh in onSaveSigner - see comment there.
                        this.loadSendImpact();
                    }
                } finally {
                    this.removingSignerId = null;
                }
            });
        },

        // ── Signer reorder (drag + up/down) ──

        onDragStart(index, event) {
            this.dragIndex = index;
            event.dataTransfer.effectAllowed = 'move';
        },

        onDragOver(index) {
            if (this.dragIndex === null || this.dragIndex === index) return;
            this.dragOverIndex = index;
        },

        onDrop(targetIndex) {
            if (this.dragIndex === null || this.dragIndex === targetIndex) {
                this.dragOverIndex = null;
                return;
            }
            this.performReorder(this.dragIndex, targetIndex);
            this.dragIndex = null;
            this.dragOverIndex = null;
        },

        onDragEnd() {
            this.dragIndex = null;
            this.dragOverIndex = null;
        },

        async moveSigner(currentIndex, direction) {
            const targetIndex = currentIndex + direction;
            if (targetIndex < 0 || targetIndex >= this.phase2Signers.length) return;
            await this.performReorder(currentIndex, targetIndex);
        },

        async performReorder(fromIndex, toIndex) {
            // Reorder within Phase 2 only, preserving other phases in their natural order
            const p2 = [...this.phase2Signers];
            const [moved] = p2.splice(fromIndex, 1);
            p2.splice(toIndex, 0, moved);

            // Build full list sorted by phase, with Phase 2 in new order
            const sorted = [...this.signers].sort((a, b) => {
                const phaseA = (SIGNER_PHASES[a.type] || SIGNER_PHASES.Signer).phase;
                const phaseB = (SIGNER_PHASES[b.type] || SIGNER_PHASES.Signer).phase;
                if (phaseA !== phaseB) return phaseA - phaseB;
                if (phaseA === 2) return p2.indexOf(a) - p2.indexOf(b);
                return 0;
            });

            const success = await this.reorderSigners(sorted.map(s => s.id));
            if (!success && this.error) {
                this.$q.notify({ type: 'negative', message: this.error });
                this.clearError();
            }
        },

        async reorderSignersByPhase() {
            const sorted = [...this.signers].sort((a, b) => {
                const phaseA = (SIGNER_PHASES[a.type] || SIGNER_PHASES.Signer).phase;
                const phaseB = (SIGNER_PHASES[b.type] || SIGNER_PHASES.Signer).phase;
                if (phaseA !== phaseB) return phaseA - phaseB;
                return a.routingOrder - b.routingOrder;
            });
            const ids = sorted.map(s => s.id);
            const currentIds = this.signers.map(s => s.id);
            if (JSON.stringify(ids) !== JSON.stringify(currentIds)) {
                await this.reorderSigners(ids);
            }
        },

        editSigner(signer) {
            this.editingSignerId = signer.id;
            const hasGroup = !!signer.signingGroupId;
            this.signerForm = {
                name: signer.name || '',
                email: isPlaceholderEmail(signer.email) ? '' : (signer.email || ''),
                type: signer.type || 'Signer',
                authMethod: signer.authMethod || 'Email',
                requiredAuthMethods: signer.requiredAuthMethods || [],
                accessCode: '',
                phone: signer.phoneForSms || signer.phone || '',
                maxKbaAttempts: signer.maxKbaAttempts || 2,
                signingGroupId: signer.signingGroupId || null,
                assignmentMode: hasGroup ? 'group' : 'individual',
                paymentAmount: signer.paymentAmount || null,
                paymentDescription: signer.paymentDescription || '',
                paymentTaxCodeId: signer.paymentTaxCodeId || null
            };
            this.ensureSigningGroupsLoaded();
            this.showAddSignerDialog = true;
        },

        isValidSmsPhone(val) {
            if (!val) return false;
            const digits = val.replace(/\D/g, '');
            return digits.length === 10;
        },

        generateAccessCode() {
            const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
            let code = '';
            for (let i = 0; i < 6; i++) {
                code += chars.charAt(Math.floor(Math.random() * chars.length));
            }
            this.signerForm.accessCode = code;
        },

        toggleAuthMethod(value, checked) {
            const methods = this.signerForm.requiredAuthMethods;
            if (checked) {
                if (!methods.includes(value)) methods.push(value);
            } else {
                const idx = methods.indexOf(value);
                if (idx >= 0) methods.splice(idx, 1);
            }
            // Update legacy authMethod for backward compat (first non-Email method, or Email if none)
            this.signerForm.authMethod = methods.length > 0 ? methods[0] : 'Email';
        },

        isAuthMethodAllowed(value) {
            if (value === 'Sms') return this.allowSmsAuth;
            if (value === 'Kba') return this.allowKbaAuth;
            if (value === 'IdVerification') return this.allowIdVerification;
            return true; // AccessCode and others always allowed
        },

        authMethodUpgradeHint(value) {
            if (value === 'Sms') return 'SMS authentication requires a plan upgrade';
            if (value === 'Kba') return 'KBA verification requires a plan upgrade';
            if (value === 'IdVerification') return 'ID Verification requires a plan upgrade';
            return '';
        },

        onSignerTypeChanged(newType) {
            if (newType !== 'Signer' && newType !== 'InPersonSigner') {
                this.signerForm.authMethod = 'Email';
                this.signerForm.requiredAuthMethods = [];
                this.signerForm.accessCode = '';
                this.signerForm.phone = '';
            }
        },

        closeSignerDialog() {
            this.showAddSignerDialog = false;
            this.editingSignerId = null;
            this.signerForm = {
                name: '',
                email: '',
                type: 'Signer',
                authMethod: 'Email',
                requiredAuthMethods: [],
                accessCode: '',
                phone: '',
                maxKbaAttempts: 2,
                signingGroupId: null,
                assignmentMode: 'individual',
                paymentAmount: null,
                paymentDescription: '',
                paymentTaxCodeId: null
            };
        },

        async ensureSigningGroupsLoaded() {
            if (this.signingGroupsLoaded) return;
            try {
                const response = await signingGroupsApi.list({ pageSize: 200 });
                const data = response.data || {};
                const groups = Array.isArray(data) ? data : (data.rows || data.items || []);
                this.signingGroupOptions = groups.map(g => ({
                    label: `${g.name} (${g.memberCount} member${g.memberCount !== 1 ? 's' : ''})`,
                    value: g.id
                }));
                this.signingGroupsLoaded = true;
            } catch (err) {
                console.error('Failed to load signing groups:', err);
            }
        },

        async openCcGroupDialog() {
            await this.ensureSigningGroupsLoaded();
            this.ccGroupSelected = null;
            this.ccGroupDialogOpen = true;
        },

        async onCcEntireGroup() {
            if (!this.ccGroupSelected) return;
            this.ccGroupLoading = true;
            try {
                const membersResponse = await signingGroupsApi.listMembers(this.ccGroupSelected);
                const members = (membersResponse.data || []).filter(m => m.email?.trim());
                if (members.length === 0) {
                    this.$q.notify({ type: 'warning', message: 'This group has no members with valid email addresses.' });
                    return;
                }

                const existingCcEmails = new Set(
                    this.signers.filter(s => s.type === 'CarbonCopy').map(s => s.email?.toLowerCase()).filter(Boolean)
                );

                const toAdd = members.filter(m => !existingCcEmails.has(m.email.toLowerCase()));
                if (toAdd.length === 0) {
                    this.$q.notify({ type: 'info', message: 'All group members are already CC recipients.' });
                    this.ccGroupDialogOpen = false;
                    return;
                }

                const results = await Promise.allSettled(
                    toAdd.map(member => this.addSigner({
                        name: member.name,
                        email: member.email,
                        type: 'CarbonCopy',
                        authMethod: 'Email'
                    }))
                );

                const added = results.filter(r => r.status === 'fulfilled').length;
                const failed = results.filter(r => r.status === 'rejected').length;
                const groupLabel = this.signingGroupOptions.find(g => g.value === this.ccGroupSelected)?.label || 'group';

                if (failed > 0 && added > 0) {
                    this.$q.notify({ type: 'warning', message: `Added ${added} CC recipient${added !== 1 ? 's' : ''} from ${groupLabel}, but ${failed} could not be added.` });
                } else if (added > 0) {
                    this.$q.notify({ type: 'positive', message: `Added ${added} CC recipient${added !== 1 ? 's' : ''} from ${groupLabel}.` });
                } else {
                    this.$q.notify({ type: 'negative', message: 'Failed to add CC recipients. Please try again.' });
                }
                this.ccGroupDialogOpen = false;
            } catch (err) {
                console.error('Failed to CC group:', err);
                this.$q.notify({ type: 'negative', message: err.displayMessage || 'An error occurred while adding group members.' });
            } finally {
                this.ccGroupLoading = false;
            }
        },

        isSignerPlaceholder(signer) {
            return isPlaceholderEmail(signer.email);
        },

        // Pre-send gauntlet — thin wrapper that delegates to the shared service.
        // The wizard's persist hook is saveBasicInfo() since the IBP flag lives
        // on basicInfo and the PATCH is package-wide.
        async _runWizardGauntletAsync() {
            const store = usePackageWizardStore();
            const result = await runPreSendGauntletAsync({
                signers: this.signers,
                enforceSigningOrder: this.basicInfo.enforceSigningOrder,
                enableInstantBankTransfer: this.basicInfo.enableInstantBankTransfer,
                paymentCollectionEnabled: this.paymentCollectionEnabled,
                $q: this.$q,
                ibtDialogRef: this.$refs.ibtDialog,
                persistIbtChangeAsync: async newValue => {
                    store.basicInfo.enableInstantBankTransfer = newValue;
                    if (newValue) store.basicInfo.enforceSigningOrder = true;
                    return await this.saveBasicInfo();
                },
                onEditSigners: () => { this.step = 4; }
            });
            return result.ok;
        },

        async onSendPackage() {
            const gauntletPassed = await this._runWizardGauntletAsync();
            if (!gauntletPassed) return;

            this.$q.loading.show({ message: 'Sending package...' });
            try {
                const success = await this.sendPackage();
                if (success) {
                    this.$q.notify({ type: 'positive', message: 'Package sent successfully!' });
                    this._navigateAfterDone({ type: 'sent', packageGuid: this.packageId });
                    return;
                }

                // Free-tier saved-PM consent intercept: 402 OverageConsentRequired
                // sets `overageConsentPreview` on the store. Open the dialog in
                // saved-pm mode with that preview - the dialog will show
                // "Charge $X.XX to {brand} ending {last4}?" and call
                // /overage/confirm-and-send on confirm.
                if (this.overageConsentPreview) {
                    this.$q.loading.hide();
                    this.showPrePayDialog = true;
                    return;
                }

                // Free-tier no-saved-PM intercept: the existing message-pattern
                // match opens the Stripe Elements dialog. This branch fires when
                // the server's HandleSynchronousPrepayAsync ran ChargeStripeAsync
                // and got back "No default payment method on file" - surfaced as
                // a plain { message } with the word "payment" in it.
                //
                // Distinguishing path-of-origin:
                //   - Structured validate errors (PAYMENT_STRIPE_NOT_CONFIGURED,
                //     PAYMENT_TAX_ELECTION_REQUIRED, PAYMENT_TAX_SETUP_INCOMPLETE,
                //     PAYMENT_DESCRIPTION_REQUIRED, etc.) carry errorCode. These
                //     indicate config/setup issues the sender must fix - show the
                //     validation dialog, never route to prepay.
                //   - Post-validate OverageDraw failures (Free-tier ran out of
                //     credits, no saved card) come through the catch block as
                //     plain { message } objects WITHOUT errorCode. That's the
                //     only case the prepay dialog is correct for.
                const validationErrors = this.serverValidationErrors || [];
                const anyStructuredError = validationErrors.some(e => e.errorCode);
                const hasPrepayCandidate = validationErrors.some(
                    e => /payment|limit/i.test(e.message || '')
                );
                if (!anyStructuredError && hasPrepayCandidate && validationErrors.length === 1) {
                    // Clear the validation dialog that would otherwise open and
                    // route to the pre-payment flow instead.
                    const store = usePackageWizardStore();
                    store.serverValidationErrors = [];
                    this.$q.loading.hide();
                    this.showPrePayDialog = true;
                    return;
                }

                if (this.error) {
                    this.$q.notify({ type: 'negative', message: this.error });
                    this.clearError();
                }
            } finally {
                this.$q.loading.hide();
            }
        },

        /** Called by PerPackagePrepayDialog when the charge + send succeed. */
        onPrePaySuccess() {
            this.$q.notify({ type: 'positive', message: 'Payment received - package sent!' });
            this._navigateAfterDone({ type: 'sent', packageGuid: this.packageId });
        },

        /**
         * Post-send routing. In the normal browser context this navigates to
         * the package details page. When the editor is embedded inside an
         * Outlook task-pane <code>displayDialogAsync</code> window (window
         * config flag <code>isOutlookDialog</code>, set by Edit.cshtml when
         * <code>?context=outlook-dialog</code> is in the URL — pinned literal
         * per <c>PortalHandoffService.AllowedTargetPathRegex</c>), we instead
         * <code>messageParent</code> back to the Outlook pane so the pane can
         * close the dialog and show "Sent!". Office.js is conditionally
         * loaded by the cshtml in that mode.
         *
         * Plan: ~/.claude/plans/integration-annotation-gap-platform-primitives.md §4 B5 (final).
         */
        _navigateAfterDone(payload) {
            const inOutlookDialog = window._pageConfig?.isOutlookDialog === true;
            const hasOfficeUi = typeof window.Office !== 'undefined'
                && window.Office?.context?.ui?.messageParent;
            if (inOutlookDialog && hasOfficeUi) {
                try {
                    window.Office.context.ui.messageParent(JSON.stringify(payload));
                    // The pane's DialogMessageReceived handler closes the
                    // dialog. Returning here without navigating keeps the
                    // editor on screen for the ~tens of milliseconds before
                    // close, which is the right UX (no jarring redirect).
                    return;
                } catch (err) {
                    // messageParent throws if invoked outside an Office dialog
                    // context (e.g. someone opened /Edit?context=outlook-dialog
                    // directly in a browser). Fall through to normal navigation
                    // as a safe default.
                    console.warn('[PackagesBuilder] messageParent failed; falling back to navigation:', err);
                }
            }
            window.location.href = `/Packages/${this.packageId}`;
        },

        startScheduleClock() {
            this._updateScheduleClock();
            this.scheduleClockTimer = setInterval(() => this._updateScheduleClock(), 1000);
        },

        stopScheduleClock() {
            if (this.scheduleClockTimer) {
                clearInterval(this.scheduleClockTimer);
                this.scheduleClockTimer = null;
            }
        },

        _updateScheduleClock() {
            const now = new Date();
            const tz = getTenantTimeZone() || 'UTC';
            this.scheduleCurrentTime = now.toLocaleTimeString([], {
                hour: '2-digit', minute: '2-digit', second: '2-digit',
                timeZone: tz
            });
        },

        // Schedule Send entry point. Run the same pre-send gauntlet that Send Now
        // runs (payment validation + branding disclosure + IBP offer) BEFORE
        // opening the time-picker dialog. A scheduled package skipping these
        // checks was a real bug — a tenant could schedule a package missing
        // disclosure config or with a stale IBP decision, and the actual send
        // would land hours/days later with no chance for the sender to course-
        // correct.
        async onScheduleSendClicked() {
            const gauntletPassed = await this._runWizardGauntletAsync();
            if (!gauntletPassed) return;
            this.showScheduleDialog = true;
        },

        async onSchedulePackage() {
            if (!this.scheduledDate || !this.scheduledTime) return;
            if (this.scheduleTimeError) return;

            // Convert 12h time (hh:mm AM/PM) to 24h for the server
            const time24 = this._convertTo24h(this.scheduledTime);
            if (!time24) return;

            // Send raw datetime without timezone - server converts using tenant timezone
            const sendTime = `${this.scheduledDate}T${time24}:00`;
            this.$q.loading.show({ message: 'Scheduling package...' });
            try {
                const success = await this.schedulePackage(sendTime);
                if (success) {
                    this.showScheduleDialog = false;
                    this.$q.notify({ type: 'positive', message: 'Package scheduled for send' });
                    window.location.href = '/Packages';
                } else if (this.error) {
                    this.$q.notify({ type: 'negative', message: this.error });
                    this.clearError();
                }
            } finally {
                this.$q.loading.hide();
            }
        },

        // (IBP dialog handlers moved into the shared <IbtOfferDialog> component
        //  and the persistence/decision logic into preSendGauntlet.js.)

        // IBP toggle dispatcher. Receives the toggle's PROPOSED new value
        // (Quasar emits @update:model-value before the store actually changes,
        // since the toggle is controlled via :model-value, not v-model).
        //
        // Enabling routes through classifyAndRunIbtDialogAsync — the same
        // classifier the send-time gauntlet uses — so the sender sees the
        // appropriate dialog mode (offer / fixup / conflict) for their current
        // signer configuration. Accept persists true; decline / edit leave
        // the store at false and the toggle visually reverts.
        //
        // Disabling routes through confirmDisableIbtInWizard, which preserves
        // prior signer/order adjustments per invariant 19.
        async onIbpToggleChange(newValue) {
            if (newValue) {
                await this._enableIbpFromToggleAsync();
            } else {
                this.confirmDisableIbtInWizard();
            }
        },

        async _enableIbpFromToggleAsync() {
            const outcome = await classifyAndRunIbtDialogAsync({
                signers: this.signers,
                enforceSigningOrder: this.basicInfo.enforceSigningOrder,
                enableInstantBankTransfer: this.basicInfo.enableInstantBankTransfer,
                paymentCollectionEnabled: this.paymentCollectionEnabled,
                ibtDialogRef: this.$refs.ibtDialog
            });

            // 'skip' fires when there's nothing to dialog about (no payers
            // configured yet). The sender's intent is still to enable IBP, so
            // persist true; the send-time validator will catch the missing
            // payer if they never add one.
            if (outcome === 'skip' || outcome === 'accept') {
                const store = usePackageWizardStore();
                store.basicInfo.enableInstantBankTransfer = true;
                store.basicInfo.enforceSigningOrder = true;
                const saved = await this.saveBasicInfo();
                if (!saved) {
                    // Revert local store on persist failure so the toggle
                    // visually goes back to OFF and the user sees the error.
                    store.basicInfo.enableInstantBankTransfer = false;
                    this.$q.notify({
                        type: 'negative',
                        message: this.error || 'Could not turn on Instant Bank Payments.'
                    });
                    this.clearError();
                } else {
                    this.$q.notify({
                        type: 'positive',
                        message: 'Instant Bank Payments turned on for this package.'
                    });
                }
                return;
            }

            if (outcome === 'edit') {
                this.step = 4;
                return;
            }

            // 'decline' or 'disable' — no persist. Toggle visually reverts
            // because basicInfo.enableInstantBankTransfer is still false.
        },

        // Disable confirm. Preserves prior signer/order adjustments per
        // invariant 19 (disable is not a structural revert).
        confirmDisableIbtInWizard() {
            this.$q.dialog({
                title: 'Turn off Instant Bank Payments?',
                message: 'This package will switch to standard card payment. Signing order and signer positions stay as they are. You can change them in the wizard.',
                cancel: { label: 'Keep enabled', flat: true },
                ok: { label: 'Turn off', color: 'primary' },
                persistent: true
            }).onOk(async () => {
                const store = usePackageWizardStore();
                store.basicInfo.enableInstantBankTransfer = false;
                const saved = await this.saveBasicInfo();
                if (!saved) {
                    // Revert so the toggle snaps back to ON visually.
                    store.basicInfo.enableInstantBankTransfer = true;
                    this.$q.notify({
                        type: 'negative',
                        message: this.error || 'Could not update Instant Bank Payments setting.'
                    });
                    this.clearError();
                } else {
                    this.$q.notify({ type: 'positive', message: 'Instant Bank Payments turned off for this package.' });
                }
            });
        },

        async saveScheduledChanges() {
            // Validate then navigate back - edits are already saved via step Continue buttons
            const store = usePackageWizardStore();
            store.sending = true;
            try {
                const validation = await packagesApi.validate(this.packageId);
                if (!validation.data.isValid) {
                    store.serverValidationErrors = validation.data.errors;
                    return;
                }
                this.$q.notify({ type: 'positive', message: 'Changes saved. Package remains scheduled.' });
                this._navigateAfterDone({ type: 'scheduled', packageGuid: this.packageId });
            } catch (err) {
                this.$q.notify({ type: 'negative', message: err.response?.data?.message || 'Failed to validate package' });
            } finally {
                store.sending = false;
            }
        },

        async saveAsDraft() {
            if (this.isScheduledEdit) {
                try {
                    await packagesApi.cancelSchedule(this.packageId);
                    this.$q.notify({ type: 'positive', message: 'Schedule cancelled. Package reverted to Draft.' });
                } catch (err) {
                    this.$q.notify({ type: 'negative', message: err.response?.data?.message || 'Failed to cancel schedule' });
                    return;
                }
            } else {
                this.$q.notify({ type: 'positive', message: 'Package saved as draft' });
            }
            window.location.href = this.packageId ? `/Packages/${this.packageId}` : '/Packages';
        },

        _convertTo24h(time12) {
            if (!time12) return null;
            // Parse "hh:mm AM" or "hh:mm PM" from q-time format
            const match = time12.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
            if (!match) return null;
            let [, h, m, period] = match;
            h = parseInt(h, 10);
            if (period.toUpperCase() === 'PM' && h !== 12) h += 12;
            if (period.toUpperCase() === 'AM' && h === 12) h = 0;
            return `${String(h).padStart(2, '0')}:${m}`;
        },

        futureDateOnly(date) {
            const tz = getTenantTimeZone() || 'UTC';
            const today = new Date().toLocaleDateString('en-CA', { timeZone: tz }).replace(/-/g, '/');
            return date >= today;
        },

        getDocumentName(documentId) {
            const doc = this.documents.find(d => d.id === documentId);
            return doc?.name || 'Unknown';
        },

        onAnnotationsChanged(fields) {
            const store = usePackageWizardStore();
            store.annotations = fields.map(f => ({
                id: f.id,
                documentId: f.documentId,
                type: f.type,
                signerIndex: this.fieldEditorSigners[f.signerIndex]?.routingOrder ?? f.signerIndex,
                pageNumber: f.pageNumber,
                x: f.x,
                y: f.y,
                width: f.widthPt,
                height: f.heightPt,
                required: f.required,
                mustBeChecked: f.mustBeChecked,
                label: f.label,
                defaultValue: f.defaultValue,
                options: f.options,
                group: f.group,
            }));
        },

        getDocIcon(extOrFileName) {
            if (!extOrFileName) return 'article';
            const e = extOrFileName.toLowerCase();
            if (e === '.pdf' || e.endsWith('.pdf')) return 'picture_as_pdf';
            if (e === '.docx' || e === '.doc' || e.endsWith('.docx') || e.endsWith('.doc')) return 'article';
            return 'article';
        },

        getDocIconColor(extOrFileName) {
            if (!extOrFileName) return 'grey';
            const e = extOrFileName.toLowerCase();
            if (e === '.pdf' || e.endsWith('.pdf')) return 'red';
            if (e === '.docx' || e === '.doc' || e.endsWith('.docx') || e.endsWith('.doc')) return 'blue';
            return 'grey';
        },

        formatFileSize(bytes) {
            if (!bytes || bytes === 0) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },

        getSignerTypeColor(type) {
            const colors = {
                'Signer': 'primary',
                'CarbonCopy': 'grey',
                'CertifiedDelivery': 'teal',
                'InPersonSigner': 'teal',
                'Approver': 'orange',
                'Editor': 'cyan'
            };
            return colors[type] || 'grey';
        },

        getAnnotationIcon(type) {
            const icons = {
                'Signature': 'draw',
                'Initials': 'draw',
                'DateSigned': 'calendar_today',
                'Text': 'notes',
                'Checkbox': 'check_box',
                'Dropdown': 'arrow_drop_down'
            };
            return icons[type] || 'short_text';
        },

        getAnnotationColor(type) {
            const colors = {
                'Signature': 'primary',
                'Initials': 'secondary',
                'DateSigned': 'cyan',
                'Text': 'grey-8',
                'Checkbox': 'orange',
                'Dropdown': 'teal'
            };
            return colors[type] || 'grey';
        },

        onTaxCodeFilter(needle, update) {
            update(() => {
                const n = (needle || '').trim().toLowerCase();
                if (!n) {
                    this.filteredTaxCodes = [];
                    return;
                }
                this.filteredTaxCodes = this.taxCodes.filter(c =>
                    (c.name || '').toLowerCase().includes(n)
                    || (c.id || '').toLowerCase().includes(n)
                    || (c.description || '').toLowerCase().includes(n)
                );
            });
        }
    },

    watch: {
        step: {
            // immediate: true so an edit session that resumes directly on step 4
            // (e.g. refresh, deep link, or returning to a package that was already
            // past the field-placement gate) still triggers the credit-impact
            // fetch on mount. Without this the watcher only fires on subsequent
            // step transitions and the preview panel stays empty.
            immediate: true,
            handler(newStep) {
                if (newStep === 3) {
                    this.ensureTagsLoaded();
                }
                if (newStep === 4 && this.documentsWithPages.length === 0 && this.allDocsReady) {
                    this.loadDocumentPages();
                }
                // Refresh credit impact each time the user enters Review & Send, since
                // signer auth methods (which drive credit needs) may have changed.
                if (newStep === 4) {
                    this.loadSendImpact();
                }
            }
        },

        // Step 4 is "Signers & Review" - signers are added/edited/removed entirely
        // within step 4, so the step watcher fires before any signers exist and we
        // need to re-fetch send-impact whenever the signer roster's credit-relevant
        // fields change. Gated on step === 4 so we don't waste API calls during
        // package load (loadPackage hydrates signers before user reaches step 4).
        creditRelevantSignerSignature() {
            if (this.step === 4 && this.packageId) {
                this.loadSendImpact();
            }
        },
        allDocsReady(ready) {
            if (ready && this.step === 4 && this.documentsWithPages.length === 0) {
                this.loadDocumentPages();
            }
        },
        'signerForm.type'(newType) {
            // Reset to individual mode when switching to CarbonCopy (no action to delegate)
            if (newType === 'CarbonCopy') {
                this.signerForm.assignmentMode = 'individual';
                this.signerForm.signingGroupId = null;
            }
            // Clear payment when switching to a non-payer type
            if (newType !== 'Signer' && newType !== 'InPersonSigner') {
                this.signerForm.paymentAmount = null;
            }
        }
    },

    beforeUnmount() {
        this._stopPolling();
        this.stopScheduleClock();
    },

    async mounted() {
        // Pre-warm MSAL.js so the OneDrive picker's click-to-window.open()
        // chain stays under Chrome's transient-user-activation window. Fire
        // and forget; errors surface on the actual click.
        prewarmOneDrive();

        const config = window._pageConfig || {};
        const packageGuid = config.packageGuid;
        const templateId = config.templateId;

        if (packageGuid) {
            // Edit mode
            this.initialLoading = true;
            await this.loadPackage(packageGuid);
            this.initialLoading = false;

            if (this.error) {
                this.$q.notify({ type: 'negative', message: this.error });
                this.clearError();
            } else if (window.location.hash === '#fields') {
                // Deep-link from portal-handoff magic link (Integration plan §3 P1).
                // The handoff mint sites append #fields to bounce the user
                // straight to the annotation-placement step (step 4) instead
                // of step 1 — saves four Next-button clicks on what's
                // supposed to be a one-click "fix the fields" UX.
                // The #fields fragment is allowlisted in
                // PortalHandoffService.AllowedTargetPathRegex (specific
                // literal, no arbitrary client-controlled hash).
                //
                // Set step regardless of allDocsReady: if docs are still
                // processing, the existing watchers at allDocsReady +
                // step === 4 fire loadDocumentPages once they're Ready,
                // and the field-placement UI renders its standard loading
                // state in the meantime. Same UX as if the user clicked
                // the q-stepper header to jump forward manually.
                this.step = 4;
            }
        } else if (templateId) {
            // Create from template
            this.initialLoading = true;
            await this.initFromTemplate(templateId);
            this.initialLoading = false;

            if (this.error) {
                this.$q.notify({ type: 'negative', message: this.error });
                this.clearError();
            }
        } else {
            // Create from scratch
            this.initFromScratch();
        }

        // Check subscription for payment collection feature
        try {
            const subRes = await this.$api.get('/subscription');
            const sub = subRes.data?.subscription;
            this.paymentCollectionAllowed = sub?.allowPaymentCollection === true;
            this.signingGroupsAllowed = sub?.allowSigningGroups === true;
            this.allowSmsAuth = sub?.allowSmsAuth !== false;
            this.allowKbaAuth = sub?.allowKbaAuth !== false;
            this.allowIdVerification = sub?.allowIdVerification !== false;
        } catch {
            // Default to allowed if check fails
        }

        // Tax-collection election. Only meaningful when payment collection is
        // enabled on the plan (otherwise the payer toggle is disabled anyway).
        // Undeclared blocks the payer toggle with a tooltip pointing to
        // Payment Settings - mirrors the server-side gate in
        // PackageService.ValidatePackageFullAsync.
        if (this.paymentCollectionAllowed) {
            try {
                const taxCfgRes = await this.$api.get('/payment-settings/tax-config');
                this.taxElection = taxCfgRes.data?.election || null;
            } catch {
                // Non-fatal - leave taxElection null; server gate is authoritative.
            }
        }

        // Load Stripe tax-code catalogue for the per-package picker. Only fetched
        // when payment collection is enabled AND the tenant has opted into Stripe
        // Tax - otherwise the picker isn't rendered and we'd be making a wasted call.
        if (this.paymentCollectionAllowed && this.taxCollectionEnabled) {
            this.loadingTaxCodes = true;
            try {
                const res = await this.$api.get('/payment-settings/tax-codes');
                this.taxCodes = res.data.codes || [];
            } catch {
                // Non-fatal - picker just shows empty.
            } finally {
                this.loadingTaxCodes = false;
            }
        }
    }
};
</script>

<style scoped>
.q-card {
    border-radius: 8px;
}
code {
    background: #e8e8e8;
    padding: 2px 6px;
    border-radius: 3px;
    font-size: 0.85em;
}
.signer-drag-over {
    background-color: rgba(31, 157, 85, 0.08);
    border-top: 2px solid #1F9D55;
}
.signer-dragging {
    opacity: 0.5;
}
.cursor-grab {
    cursor: grab;
}
.cursor-grab:active {
    cursor: grabbing;
}
.signer-swap-move {
    transition: transform 0.35s ease;
}
</style>

<style>
/* Horizontal stepper styling */
.packages-stepper {
    border-radius: 12px !important;
}

.packages-stepper .q-stepper__header {
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.packages-stepper .q-stepper__step-content {
    padding: 24px 16px;
}
</style>
